/**
 * Full Conversation Session Service
 *
 * Maneja la comunicación WebSocket con el agente Full Conversation (protocol fc-v5).
 * Responsabilidades:
 * - Conectar/desconectar WebSocket
 * - Recibir comandos del agente (assistant_logical_complete, mic_open, next_question, etc.)
 * - Enviar audio del usuario al agente
 * - Manejar estados de la entrevista
 * - Detectar silencio para fin de turno
 */

import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, Observable, BehaviorSubject, firstValueFrom } from 'rxjs';
import { ConfigService } from '../../../services/config.service';
import { coalesceLog, coalesceLogWith, clearCoalesced } from '../../../shared/utils/coalesced-logger';
import { RumClientMetricReporter } from '../../../shared/utils/rum-client-metrics';
import { AckCache } from './protocol/ack-cache';
import {
  ClientAckPayload,
  CRITICAL_ACK_TYPES_V5,
  WsProtocolVersion,
} from './protocol/envelope.types';
import { ProtocolNegotiator } from './protocol/protocol-negotiator';
import {
  FC_V5_CLIENT_CAPABILITIES,
  FC_V5_PROTOCOL,
  FcV5ClientProctoringEvent,
  FcV5ProctoringKind,
} from './protocol/fc-v5.types';
import { adaptFcV5Command } from './protocol/fc-v5-adapter';
import { FcLowRuntime } from './low/fc-low-runtime';
import {
  FcLowAnswerTimer,
  FcLowApiService,
  FcLowIntroProgress,
  FcLowLifeline,
  FcLowLifelineTrigger,
} from './low/fc-low-api.service';
import type { AffectSnapshot } from './affect/affect.types';
import { FcBrowserTtsService } from './low/fc-browser-tts.service';
import { VcScorerService } from '../../../services/vc-scorer.service';
// AJV se carga vía dynamic import para validar fc-v5 sin inflar el path inicial.
import type { ValidateFunction } from 'ajv';

/** Razón de la supresión outbound para trazabilidad en debug snapshots. */
export type SuppressReason = 'greeting' | 'narration' | 'manual';
export type AudioStopMode = 'immediate' | 'drain' | 'reset';

export type AgentState =
  | 'connecting'
  | 'reconnecting'
  | 'initializing'
  | 'narrating'
  | 'preparing'
  | 'listening'
  | 'processing'
  | 'eva_thinking'
  | 'follow_up'
  | 'transitioning'
  | 'draining_audio'
  | 'completed'
  | 'error';

export interface InterviewEvaluation {
  overallSummary: string;
  technicalLevel: 'junior' | 'mid' | 'senior' | 'principal' | 'unclear';
  confidenceScore: number;
  dimensions: {
    [key: string]: {
      score: number;
      observations: string[];
    };
  };
  strengths: string[];
  growthAreas: string[];
  knowledgeAreas: {
    areaName: string;
    summary: string;
    evidences: string[];
    confidence: number;
    depthScore: number;
  }[];
  recommendation: string;
  redFlags: string[];
}

export interface QuestionSpecial {
  type: 'code' | 'none' | string;
  language: string;
  content: string;
}

export interface AgentCommand {
  type: 'session_ack' |
        // protocol fc-v5 — events from BE → FE (snake_case)
        'next_question' |
        'interview_complete' | 'session_expired' | 'error' |
        'transcription' | 'interrupted' |
        'ping' |
        'assistant_turn_started' | 'assistant_logical_complete' |
        'mic_open' | 'user_turn_complete' |
        'followup_requested' |
        'criteria_eval' |
        'capture_stalled' |
        'state_resync' |
        'audio_gap_detected' |
        // LOW-only: boot resume tras recarga trae elapsed_seconds — solo
        // reanuda el cronometro del shell, sin los efectos de state_resync
        // (que son de reconexion WS y no aplican al runtime LOW sin socket).
        'timer_resync' |
        // FE-internal commands (emitted by the service to drive the component)
        'ERROR' |
        'RECONNECTING' |
        'RECONNECT_FAILED';
  event_id?: string;
  server_seq?: number;
  sent_at?: string;
  protocol?: string;
  requires_ack?: boolean;
  provider_epoch?: number;
  text?: string;
  transcript?: string;
  display_text?: string;
  display_revision?: string;
  turn_kind?: string;
  // LOW-only: assistant_turn_started de una RE-narración (comando repeat). El
  // shell re-narra el audio pero NO debe empujar una card nueva al thread
  // (followup/rephrase ya están pintados) — evita contrapreguntas duplicadas.
  renarration?: boolean;
  // LOW-only: qué hizo el BE con el prompt (ver FcLowResponse.prompt_action).
  // 'repose' = mismo prompt re-narrado (el thread NO cambia); 'rewrite_followup'
  // = reemplaza en sitio la última contrapregunta.
  prompt_action?: 'new_followup' | 'rewrite_question' | 'rewrite_followup' | 'repose';
  role?: 'agent' | 'user';
  evaluation?: InterviewEvaluation;
  questionIndex?: number;
  question_index?: number;
  question_text?: string;
  areaName?: string;
  knowledge_name?: string;
  total_questions?: number;
  special?: QuestionSpecial;
  message?: string;
  // session_ack / followup_requested traen phase para
  // sincronizar la fase de narración (greeting/question/farewell).
  phase?: string;
  // Eventos del refactor FSM (Fase A/B)
  question_id?: string;
  question_idx?: number;
  knowledge_id?: string;
  assistant_turn_id?: string;
  assistantTurnId?: string;
  audio_seq_start?: number;
  audio_seq_end?: number;
  requires_playback_ack?: boolean;
  next_expected_client_event?: string;
  mic_open?: boolean;
  snapshot?: unknown;
  reason?: string;
  // VC detection (CONTRATO-VC-DETECTION.md §4.3) — solo en interview_complete,
  // 'conferencing_tool' cuando el agente suspendio por deteccion de
  // videoconferencia. Aditivo/opcional: el BE (ya mergeado) confirma que viaja
  // NULL explicito (no ausente) en cualquier otra terminacion — no tratar la
  // sola ausencia como significativa. `reason` (enum cerrado) no cambia.
  termination_reason?: string | null;
  transition_id?: string;
  missing_aspect?: string;
  followup_count?: number;
  scores?: Record<string, boolean>;
  verdict?: 'accepted' | 'followup';
  latency_ms?: number;
  server_capabilities?: {
    audio_seq_header?: boolean;
    client_audio_seq_header?: boolean;
    client_audio_magic_header?: boolean;
    [key: string]: unknown;
  };
  attempt_id?: string;
  // [ATTEMPT-ID] camelCase alias para idempotencia double-key en NEXT_QUESTION.
  attemptId?: string;
  reconnectable?: boolean;
  attempt?: number;
  max?: number;
  // Fix E (state_resync clear_audio_buffer) — el BE emite state_resync con
  // este flag cuando hace retry de la sesión S2S pre-narración. Reemplaza al
  // 'interrupted' que antes mandaba (que disparaba el gate anti-eco de
  // barge-in y métricas incorrectas).
  clear_audio_buffer?: boolean;
  reload_count?: number;
  reload_max?: number;
  reload_limit_reached?: boolean;
  elapsed_seconds?: number;
  // Timers piense/pregunta (contrato nuevo BE→FE) — solo viajan en next_question.
  // Opcionales para tolerar un backend viejo que todavía no los manda.
  thinking_time_s?: number;
  answer_budget_s?: number;
  // fc-affect-adaptive-tuning — solo viaja en mic_open. Segundos de silencio
  // tras los que el agente cierra el turno (valor REAL del settle, varía
  // 2.0-5.5s según perfil). Opcional para tolerar un backend viejo que
  // todavía no lo manda — sin él, la barra de silencio de turno del FE
  // simplemente no se muestra (ver updateTurnSilenceBar en el componente).
  // NO se inventa un default: un valor inventado mentiría sobre el settle
  // real del agente.
  turn_silence_s?: number;
  // criteria_eval (DEBUG, dev-monitor) — cómo la IA calificó cada criterio de
  // la rúbrica + por qué decidió avanzar/repreguntar en el turno. Solo llega en
  // local/dev (gated en el BE). Opcional: sin él, el panel simplemente no se
  // actualiza.
  decision_kind?: string;
  decision_reason?: string;
  followup_text?: string;
  criteria?: CriterionEvalDto[];
  // Salvavidas (LOW, PLAN_LIFELINE.md) — debug de criteria_eval: índice de
  // apuro del turno y momento de pérdida que lo evaluó.
  distress_score?: number;
  lifeline_trigger?: FcLowLifelineTrigger | null;
  // Salvavidas — estado del cupo (usados/máximo) que el runtime LOW copia del
  // payload `lifeline` del low-api en assistant_turn_started/followup_requested
  // (reason:'lifeline_hint' marca la card + el mic_open de ventana nueva).
  lifeline?: FcLowLifeline;
  // Módulo de experiencia previa (LOW) — progreso del turno; solo viaja en
  // assistant_turn_started de un turno phase:'intro' (el shell lo ve como
  // greeting). Ausente = no es turno del módulo.
  intro?: FcLowIntroProgress;
  // Solo en mic_open (LOW): la ventana se arma de CERO con los budgets que
  // trae (no se reanuda un remanente pausado). La manda el runtime en los
  // turnos del módulo de experiencia previa.
  fresh_window?: boolean;
  // Solo en mic_open (LOW): Eva preguntó "¿quieres agregar algo más?" — el
  // timer de la pregunta sigue congelado (no se reanuda el remanente).
  confirm_advance?: boolean;
  // Solo en mic_open (LOW): Eva concedió una pausa de N segundos — el timer de
  // la pregunta sigue congelado y el shell muestra la cuenta de la pausa.
  pause_s?: number;
}

export interface CriterionEvalDto {
  key: string;
  description: string;
  required: boolean;
  covered: boolean;
}

/** Snapshot que el panel dev de criterios consume (último turno evaluado). */
export interface CriteriaEvalSnapshot {
  questionIdx: number;
  questionId: string;
  decisionKind: string;
  decisionReason: string;
  missingAspect: string;
  followupText: string;
  criteria: CriterionEvalDto[];
  /** DEBUG salvavidas — índice de apuro [0,1] (ausente = BE sin el feature). */
  distressScore?: number;
  /** DEBUG salvavidas — momento de pérdida evaluado (null = ninguno). */
  lifelineTrigger?: FcLowLifelineTrigger | null;
  at: number;
}

export interface FcSessionConfig {
  testId: string;
  candidateEmail: string;
  sessionId: string;
  knowledgeId?: string;
  questionIndex?: number;
  clientReloadDetected?: boolean;
  /** high = AgentCore WS (default). low = HTTP full-conversation-low + browser TTS, same UI shell. */
  qualityMode?: 'high' | 'low';
}

/** Campo del stash de mic_open para la pausa concedida (LOW); vacío si no aplica. */
function pauseArm(pauseS: number | undefined): { pauseS?: number } {
  if (typeof pauseS !== 'number' || pauseS <= 0) return {};
  return { pauseS };
}

@Injectable({
  providedIn: 'root'
})
export class FcSessionService {
  private ws: WebSocket | null = null;
  private lowRuntime: FcLowRuntime | null = null;
  private qualityMode: 'high' | 'low' = 'high';
  private readonly rumMetrics = new RumClientMetricReporter({ enabled: () => this.configService.clientMetricsEnabled });
  /** Last connected mode; `disconnect()` resets `qualityMode` to 'high', so it cannot label late metrics. */
  private metricsQualityMode: 'high' | 'low' | null = null;
  /**
   * quality_mode=low — component sets this so decide receives adaptation_profile
   * from AffectEngine.getSnapshot().profile (WS hints are a no-op without socket).
   */
  adaptationProfileProvider: (() => string | null) | null = null;
  /**
   * quality_mode=low — component reports when a contrapregunta must NOT be
   * narrated (gloria/overtime o quedan <=15s con el candidato aún hablando).
   * El runtime lo consulta antes de narrar el followup.
   */
  followupSuppressionProvider: (() => boolean) | null = null;
  /**
   * quality_mode=low — salvavidas: snapshot del affect engine que el runtime
   * lee al cerrar cada turno para mandar `affect_signals` al low-api.
   */
  affectSnapshotProvider: (() => AffectSnapshot | null) | null = null;
  /** LOW — timer de respuesta del shell (lo setea el componente); solo lectura para el BE. */
  answerTimerProvider: (() => FcLowAnswerTimer | null) | null = null;
  private reconnectAttempts = 0;
  // AJV schema validator — initialized in every environment; fc-v5 is fail-closed.
  private envelopeValidator: ValidateFunction | null = null;
  private envelopeValidatorReady: Promise<void>;
  private envelopeValidatorFailed = false;
  // Bug AG.3 — subido de 3 a 6 para tolerar más blips de red en entrevistas
  // largas (25 preguntas pueden durar 30+ min).
  private maxReconnectAttempts = 6;
  // Fase 7 B.3 — set to true after INTERVIEW_COMPLETE to suppress post-farewell
  // reconnect triggered by a clean or unclean WS close.
  private terminatedByEnd = false;
  // Fase 7 B.4 — set to true by disconnect()/endSession() so the onclose handler
  // can distinguish an intentional close from a network drop.
  private finalizingWsClose = false;
  // Fase 8 B.1 — runtime gate: flip to true only when BE echoes
  // server_capabilities.audio_seq_header === true. Default OFF for backward-compat.
  private serverAudioSeqHeaderActive = false;
  // fc-v5 — BE can request a framed header on FE→BE PCM frames.
  // The magic prefix prevents raw PCM from being misread as metadata during
  // the session_ack race window.
  private readonly clientAudioMagicHeader = 0x46434131; // "FCA1"
  private audioHandshakeReady = false;
  private clientAudioSeqHeaderActive = false;
  private clientAudioMagicHeaderActive = false;
  private clientAudioSeq = 0;
  /** Last float RMS (0..1) from the worklet — packed as q15 on outbound PCM. */
  private lastOutboundRmsFloat = 0;
  private lastClientAudioSeq = 0;
  private lastEvaAudioSeq = 0;
  private lastAudioSeq = 0;
  private lastCaptureSeq = 0;
  private lastServerSeqApplied = 0;
  private processedEventIds = new Set<string>();
  private currentQuestionId: string | undefined;
  private currentQuestionIndex: number | undefined;
  private activeAssistantTurnId: string | undefined;
  private activeAssistantTurnStartedAtMs = 0;
  private activeAssistantLogicalCompleteAtMs = 0;
  private pendingPlaybackAck = false;
  /**
   * mic_open may carry thinking_time_s / answer_budget_s without a prior
   * next_question (greeting, followup). Stash before setMicOpen(true) so the
   * component can arm timers on micOpen$ — pendingQuestionTimerArm only arms
   * from next_question.
   */
  private pendingMicOpenTimerArm: {
    thinkingTimeS: number;
    answerBudgetS: number;
    questionIdx: number | undefined;
    /** Salvavidas (LOW) — el mic_open abre una ventana de respuesta NUEVA
     *  aunque el presupuesto anterior haya vencido (ver componente). */
    freshWindow?: boolean;
    /** LOW — Eva preguntó "¿quieres agregar algo más?": el timer de la
     *  pregunta sigue congelado mientras el candidato decide. */
    confirmAdvance?: boolean;
    /** LOW — pausa concedida: segundos de la cuenta regresiva (timer congelado). */
    pauseS?: number;
  } | null = null;
  private playbackAckSentForTurnId: string | undefined;
  private currentBackendPhase: string | undefined;
  // Fase de narración actual para gates locales de playback/supresión.
  currentNarrationPhase: 'greeting' | 'question' | 'farewell' = 'greeting';
  // Fase 9.1 — outbound audio suppression window (greeting only).
  // sendAudio skips ws.send while Date.now() < audioOutputSuppressedUntilMs.
  private audioOutputSuppressedUntilMs = 0;
  private audioOutputSuppressCount = 0;
  // Fix 2 — timestamp del último warn de ws_send_buffer_overflow (throttle 1/2s).
  private lastWsSendBufferOverflowWarnMs = 0;
  private wsAudioBackpressureWindowStartedMs = 0;
  private wsAudioBackpressureCount = 0;
  private readonly wsAudioBackpressureWindowMs = 5000;
  private readonly wsAudioBackpressureReconnectThreshold = 3;
  // Anti-eco: true cuando la supresión queda armada hasta mic_open/playback_end/resync.
  // Activado al inicio de cada narración de Eva para bloquear el feedback acústico
  // que dispara false-positive "interrupted" en el server VAD de Gemini.
  private outboundSuppressArmed_narration = false;
  // Timestamp de inicio de narración (performance.now()) — para el gate de
  // barge-in temprano (< 4s → eco casi seguro → ignorar).
  private narrationStartTs: number | null = null;
  // Watchdog anti-deadlock: epoch-ms del último chunk de audio de Eva recibido.
  // Inicializado al armar la supresión para que el watchdog no dispare antes de que
  // arranque el audio. Se actualiza con cada chunk via notifyEvaAudioActivity().
  private lastEvaAudioChunkAtMs = 0;
  // Bug A fix — diferir la liberación hasta que el playback local de Eva termine.
  // Cuando assistant_logical_complete llega pero aún hay audio agendado en el componente,
  // activamos este flag en vez de liberar inmediatamente (évita eco acústico).
  // El componente llama notifyPlaybackEnd() cuando el último source.onended dispara.
  private releaseOnPlaybackEnd = false;
  // Bug A fix — epoch-ms en que se armó la supresión con releaseOnPlaybackEnd.
  // Usado por el cap absoluto de 90s para garantizar que nunca se queda colgado.
  private suppressionArmedAtMs = 0;
  // Interval del watchdog (limpiado en releaseOutboundSuppression y disconnect).
  private suppressWatchdogInterval: any = null;
  // Fix D — split del watchdog: "Eva no empezo" (TTFT alto, normal) tiene un
  // budget mucho mayor que "Eva estaba narrando y se congelo" (deadlock real).
  // El cap absoluto de 90s (linea ~1262) sigue siendo backstop.
  // Causa D fix: mid_stream subido de 4000 → 8000ms para tolerar jitter normal
  // entre bursts de Gemini. 4s producía liberaciones prematuras cuando la cola
  // de audio del componente tenía un drain momentáneo entre bursts (≠ deadlock).
  private readonly WATCHDOG_FIRST_CHUNK_TIMEOUT_MS = 15_000;
  private readonly WATCHDOG_MID_STREAM_TIMEOUT_MS = 8_000;
  // BE ya cerró el turno lógico (assistant_logical_complete) y no llegó ningún
  // chunk: la narración fue muda con certeza, no hace falta esperar 15s.
  private readonly WATCHDOG_SILENT_TURN_TIMEOUT_MS = 2_000;
  private playbackEndDeadlineTimer: any = null;
  // Fix C — subido de 2500ms a 5000ms para reducir falsos positivos de
  // playback_end_deadline_release cuando chunks llegan rebufereados después
  // de assistant_logical_complete (wall-clock vs AudioContext clock).
  private readonly playbackEndDeadlineMs = 5000;
  // Fix C — cap absoluto desde turnCompleteTsMs. Más allá de este tope,
  // bumpPlaybackEndDeadline NO re-arma el timer (evita loop infinito).
  // Subido 15s → 30s en feature/followup-render-and-deadline-rearm: los
  // followups largos (log23, narration #2 post re-narration) llegan a 20s+
  // de duración, y el cap medido desde suppressionArmedAtMs cortaba el final
  // del audio. Ahora se mide desde turnCompleteTsMs (cap aplica a la ventana
  // post-assistant_logical_complete, no a toda la duración de la narración).
  private readonly playbackEndDeadlineMaxAgeMs = 30000;
  // Epoch-ms del último assistant_logical_complete que armó playbackEndDeadline.
  // El cap se mide desde acá, NO desde suppressionArmedAtMs (que mezclaba
  // duración total de narración con tiempo post-assistant_logical_complete).
  private turnCompleteTsMs: number | null = null;
  private reloadSignalSent = false;
  public reloadCount = 0;
  public reloadMax = 0;
  public reloadLimitReached = false;
  private sessionAckWatchdogTimer: any = null;
  // Local corre contra AWS real (latencia de internet), no Lambda-a-Lambda
  // como en prod — 5s se queda corto ahí y dispara reconexión en loop.
  private readonly sessionAckWatchdogMs =
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
      ? 15000
      : 5000;
  // Bug A fix — getter para que el componente pueda consultar si hay audio local
  // todavía sonando (lo setea el componente via notifyLocalAudioPlaying).
  private localAudioIsPlaying = false;
  // FE-2 — id de la sesión actual para detectar cambios cross-session y
  // resetear el estado de tracking (lastServerSeqApplied, processedEventIds, etc.).
  private currentSessionId: string | null = null;

  // Bug FE-2 — backoff exponencial truncado con jitter (decorrelated jitter,
  // AWS pattern). Sin jitter, N clientes reintentan en sincronía cuando
  // Bedrock Agentcore restartea → thundering herd.
  private readonly reconnectBaseDelayMs = 500;
  private readonly reconnectMaxDelayMs = 15_000;
  private lastReconnectDelayMs = 0;
  private reconnectJitterSeed = 1;
  // F1 — epoch de conexión: cada socket nuevo incrementa el contador y sus
  // handlers capturan el valor. Un callback de un socket viejo se descarta.
  private connectionEpoch = 0;
  // Bug AG.3 — umbral a partir del cual la UI muestra "Reconectando...".
  // Attempts 1-2 son silenciosos (la mayoría de blips se resuelven rápido).
  private reconnectUiThreshold = 3;
  // Bug AG.2 — timestamp del último ping recibido del backend. La UI puede
  // usarlo para detectar zombie conexiones (no se valida aquí, solo expuesto).
  private lastServerActivityTs = 0;
  private lastServerAttemptId = '';

  // -------------------------------------------------------------------------
  // Protocolo Full Conversation v5.
  // -------------------------------------------------------------------------
  // ProtocolNegotiator administra activeProtocol y provider_epoch tracking.
  // Protocolo único fc-v5.
  private readonly protocolNegotiator = new ProtocolNegotiator();
  // Cache idempotente de client_ack: replays del mismo event_id devuelven
  // exactamente el mismo payload (spec §5).
  private readonly ackCache = new AckCache();

  /** Protocolo activo. */
  get activeProtocol(): WsProtocolVersion {
    return this.protocolNegotiator.clientProtocol;
  }

  // Estado de la entrevista
  private stateSubject = new BehaviorSubject<AgentState>('connecting');
  public state$ = this.stateSubject.asObservable();
  
  // Comandos del agente
  private commandSubject = new Subject<AgentCommand>();
  public command$ = this.commandSubject.asObservable();
  
  // Transcripción visible (opcional)
  // Bug 8 fix (ARCHITECTURE_V5_CURRENT_STATE.md §7): `mode` lets a producer
  // opt into replacing the agent buffer instead of appending — see
  // `assistant_turn_started` below. Omitted `mode` defaults to 'append',
  // identical to the pre-fix behavior for the two existing emitters.
  private transcriptSubject = new Subject<{
    role: 'agent' | 'user';
    text: string;
    mode?: 'append' | 'replace';
  }>();
  public transcript$ = this.transcriptSubject.asObservable();
  // Bug 8 fix: last agent display_text pushed to the transcript panel, keyed
  // by display_revision — dedupes a re-delivered/duplicate assistant_turn_started
  // (e.g. after state_resync) and lets followup_requested (below) skip
  // re-emitting text the panel already shows.
  private lastAgentDisplayText: string | undefined;
  private lastAgentTranscriptRevision: string | undefined;
  
  // Estado de grabación
  private recordingSubject = new BehaviorSubject<boolean>(false);
  public isRecording$ = this.recordingSubject.asObservable();

  // Estado autoritativo de apertura del micrófono en protocolo fc-v5.
  private micOpenSubject = new BehaviorSubject<boolean>(false);
  public micOpen$ = this.micOpenSubject.asObservable();
  
  // Progreso de la entrevista
  private progressSubject = new BehaviorSubject<{ current: number; total: number }>({ current: 0, total: 0 });
  public progress$ = this.progressSubject.asObservable();
  
  // Área actual
  private areaSubject = new BehaviorSubject<string>('');
  public currentArea$ = this.areaSubject.asObservable();

  // criteria_eval (DEBUG dev-monitor) — último turno calificado por la IA.
  private criteriaEvalSubject = new BehaviorSubject<CriteriaEvalSnapshot | null>(null);
  public criteriaEval$ = this.criteriaEvalSubject.asObservable();
  
  // Audio recibido del agente (para reproducción en el componente)
  private audioDataSubject = new Subject<ArrayBuffer>();
  public audioData$ = this.audioDataSubject.asObservable();

  // Señal de "detener reproducción de audio" — el componente la consume.
  // 'immediate' = corte duro (barge-in/interrupción). 'drain' = dejar terminar
  // la cola ya agendada antes de limpiar (transición normal de pregunta).
  private stopAudioSubject = new Subject<AudioStopMode>();
  public stopAudio$ = this.stopAudioSubject.asObservable();

  // Fase 1 — backend emite { type:'capture_stalled' } cuando detecta ausencia
  // de capture_heartbeat. Componente usa este Subject para activar recovery.
  private captureStalledSubject = new Subject<void>();
  public captureStalled$ = this.captureStalledSubject.asObservable();

  // Bug A fix: audioContext huérfano eliminado. El componente gestiona el
  // único AudioContext (audioCtx) sincrónicamente en el click handler.

  // Detección de silencio
  private silenceTimeout: any = null;
  private silenceThresholdMs = 3000; // 3 segundos de silencio = fin de respuesta
  private lastSpeechTime = Date.now();
  private isSpeaking = false;

  // Ola 3 — funnel de métricas por turno de Eva.
  // Se resetean al inicio de cada turno.
  private evaFirstChunkSentThisTurn = false;
  private evaFirstPlaySentThisTurn = false;
  // Ola 3 — si el usuario estaba hablando (RMS > umbral) y dejó de hacerlo,
  // registra el epoch-ms del fin de voz para fe_user_speech_end.
  private userSpeechActiveForMetric = false;
  // Ola 2 — timestamp del fin de voz del usuario (RMS cae) para la métrica
  // first_audio_play_latency_ms (tiempo entre fin de habla y primer play de Eva).
  private lastUserSpeechEndTs: number | null = null;
  // [FC_METRIC] Historial de latencias por turno — capped en 20 para QA (Ola 2.4 fix).
  firstAudioPlayLatencyHistory: number[] = [];
  // D2 — ancla para la métrica incondicional: epoch-ms del primer chunk del turno
  // de Eva (notifyEvaFirstChunk). Se registra siempre (greeting incluido).
  // Elegimos el primer chunk como ancla porque es el primer evento robusto y
  // disponible en TODOS los turnos, incluyendo greeting y narraciones.
  private turnStartAnchorTs: number | null = null;
  // D2 — flag por turno: true cuando ya se emitió first_audio_play_unconditional_ms.
  private evaFirstPlayUnconditionalSentThisTurn = false;
  // D2 — historial de latencias incondicionales (capped 20, para QA/debug).
  firstAudioPlayUnconditionalHistory: number[] = [];
  // Ola 3 — flag de barge-in calculado UNA VEZ en connect().
  // Falso por defecto (Stage 1): cero cambio de comportamiento.
  bargeInEnabled = false;
  // Ola 3 — debounce timer para eva_thinking (800ms tras caída de RMS).
  private evaThinkingDebounceTimer: any = null;
  // FC Feature 1 — cap de 20s para el estado eva_thinking. Si Eva no responde
  // en 20s, ocultar el indicador para no dejarlo infinito.
  private evaThinkingCapTimer: any = null;
  static readonly FC_THINKING_CAP_MS = 20_000;
  // Ola 3 — contador de "interrupted" rechazados por eco (min-speech gate).
  // Al alcanzar 3 → auto-deshabilitar barge-in en runtime.
  echoSuspectCount = 0;

  // FC Feature 2 — Resume buffer para falsa interrupción (patrón LiveKit).
  // Al recibir 'interrupted' guardamos los chunks no reproducidos en vez de
  // descartarlos. Si la ventana de evaluación (4s) expira sin habla real,
  // chunks nuevos de Eva, o cambio de pregunta → re-encolar (falsa interrupción).
  private resumeBuffer: { data: ArrayBuffer; seq: number }[] = [];
  private resumeBufferTimestamp = 0;
  private falseInterruptTimer: any = null;
  // Budget: máximo 1 reanudación por pregunta (resetear en NEXT_QUESTION).
  private falseInterruptBudget = 1;
  // Fix RE-NARRACIÓN — último idx procesado; si NEXT_QUESTION llega con el mismo
  // idx es un reinicio del BE, no pregunta nueva: no resetear el budget.
  private lastProcessedQuestionIdx: number | undefined = undefined;
  // Señal al componente: Subject que emite los chunks del resumeBuffer cuando
  // la ventana expira sin actividad real.
  private resumeAudioSubject = new Subject<ArrayBuffer[]>();
  public resumeAudio$ = this.resumeAudioSubject.asObservable();
  // Señal al componente: releaseOutboundSuppression() es un martillo que no
  // sabe si había un piense armado (ver docblock del método) — esto le avisa
  // al componente para que, si fue SU ventana la que se barrió, decida si
  // re-armarla (ver suppressionForceReleased$ subscription en el componente).
  private suppressionForceReleasedSubject = new Subject<void>();
  public suppressionForceReleased$ = this.suppressionForceReleasedSubject.asObservable();
  // Señal de evaluación de ventana: flags seteados durante la ventana.
  private falseInterruptSawUserTranscript = false;
  private falseInterruptSawNewEvaChunk = false;
  static readonly FC_FALSE_INTERRUPT_RESUME_MS = 4_000;
  // Ola 3 Stage 2 — duración de voz local sostenida en la última ventana de 1.5s.
  // Actualizado por el componente via notifyLocalSpeechWindow() por cada chunk de voz.
  private lastLocalSpeechWindowMs = 0;
  // Tiempo del último chunk de voz local (para calcular la ventana de 1.5s).
  private localSpeechWindowStartMs = 0;
  private isLocal = window.location.hostname === 'localhost' ||
                     window.location.hostname === '127.0.0.1';

  // FE-1 — handle del setTimeout de reconnect para poder cancelarlo si
  // disconnect() o connect() llegan durante el delay (evita zombie WS).
  private reconnectTimer: any = null;

  // Watchdog de transición: tras un advance/pass o un next_question, el front
  // queda esperando el `assistant_turn_started` de la próxima pregunta ("Pasando
  // a la siguiente pregunta..."). Si ese frame no llega (Gemini colgado tras la
  // rotación de área, o socket medio-muerto por ping alto donde el server cree
  // que sigue vivo), el front quedaba atascado hasta que el usuario recargaba.
  // Este watchdog fuerza una reconexión (→ state_resync) automáticamente, que es
  // lo que hacía el reload manual. Timeout generoso para no cortar una carga
  // lenta pero legítima (rotación de área hasta 10s + gate de silencio pre-
  // narración hasta 20s + arranque de Gemini).
  private transitionWatchdogTimer: any = null;
  private readonly transitionWatchdogMs = 40_000;

  constructor(
    private ngZone: NgZone,
    private http: HttpClient,
    private configService: ConfigService,
    private fcLow: FcLowApiService,
    private fcBrowserTts: FcBrowserTtsService,
    /**
     * VC detection (CONTRATO-VC-DETECTION.md §2/ADENDA) — quality_mode=low
     * pasa esto a FcLowRuntime (connectLow) para que su conferencingLadder se
     * suscriba a state$. En 'high' este servicio no lo usa directamente acá
     * (el enforcement hablado de HIGH corre server-side, vía WS).
     */
    private vcScorer: VcScorerService,
  ) {
    this.envelopeValidatorReady = this.initAjvValidator();
  }

  private async initAjvValidator(): Promise<void> {
    try {
      // OJO: 'ajv/dist/2020', no 'ajv'. El envelope schema declara
      // $schema: draft/2020-12 y el Ajv por defecto es draft-07: compilarlo con
      // el default tira 'no schema with key or ref .../draft/2020-12/schema',
      // el catch de abajo lo absorbe y la validacion quedaria DESACTIVADA en
      // silencio (fail-open) creyendo que esta activa.
      const [ajvModule, schemaModule] = await Promise.all([
        import('ajv/dist/2020'),
        import('./protocol/envelope.schema.json'),
      ]);
      const AjvCtor = (ajvModule as any).default ?? ajvModule;
      const schema = (schemaModule as any).default ?? schemaModule;
      const ajv = new AjvCtor({ allErrors: true, strict: false });
      this.envelopeValidator = ajv.compile(schema);
      this.envelopeValidatorFailed = false;
    } catch (e) {
      this.envelopeValidatorFailed = true;
      this.envelopeValidator = null;
      console.error('[FC-PROTOCOL] AJV setup failed:', e);
    }
  }
  
  /**
   * Bug A fix — initializeAudio ya no crea un AudioContext propio.
   *
   * El componente crea UN SOLO AudioContext (audioCtx) sincrónicamente al
   * inicio del click handler, antes de cualquier await, para preservar el
   * user-gesture token de Chrome. Crear un AudioContext aquí (con su
   * setTimeout interno de 50ms en playSilentBuffer) rompía esa cadena y
   * causaba que los contextos de playback y streaming quedaran silenciados
   * en el primer intento sin reload.
   *
   * Este método se mantiene como no-op para backward-compat con cualquier
   * llamada existente. El componente lo invoca DESPUÉS de crear audioCtx.
   */
  async initializeAudio(): Promise<void> {
    console.log('[FC-SERVICE] initializeAudio: no-op (AudioContext managed by component)');
  }
  
  /**
   * Conecta al WebSocket del agente
   */
  async connect(config: FcSessionConfig): Promise<void> {
    // quality_mode=low: HTTP full-conversation-low + browser TTS. Same processCommand path, no WS/AJV.
    if (config.qualityMode === 'low') {
      await this.connectLow(config);
      return;
    }

    this.qualityMode = 'high';
    this.metricsQualityMode = 'high';
    if (this.lowRuntime) {
      this.lowRuntime.stop();
      this.lowRuntime = null;
    }

    await this.envelopeValidatorReady;
    if (this.envelopeValidatorFailed || !this.envelopeValidator) {
      console.error('[FC-PROTOCOL] Cannot connect without fc-v5 schema validator');
      this.setState('error');
      this.commandSubject.next({
        type: 'ERROR',
        message: 'No se pudo inicializar el contrato de comunicación.',
      });
      return;
    }

    const newSessionId = config.sessionId;
    if (this.ws?.readyState === WebSocket.OPEN) {
      if (this.currentSessionId === newSessionId) {
        console.log('[FC-SERVICE] Already connected');
        return;
      }
      console.warn('[FC-SERVICE] Open WebSocket belongs to a different logical session; closing before reconnect', {
        currentSessionId: this.currentSessionId,
        newSessionId,
      });
      this.disconnect();
    }

    // FE-1 — cancelar cualquier reconnect pendiente antes de arrancar uno nuevo.
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Fase 7 B.3 / B.4 — reset lockout flags at the start of every new logical session.
    this.terminatedByEnd = false;
    this.finalizingWsClose = false;

    // FE-2 — reset de estado cross-session cuando el sessionId cambia.
    // El service es singleton root: lastServerSeqApplied y processedEventIds
    // NUNCA se resetean solos entre sesiones distintas → riesgo de dup-drop.
    if (this.currentSessionId !== newSessionId) {
      console.log(`[FC-SERVICE] FE-2 session change ${this.currentSessionId} → ${newSessionId} — resetting seq/idempotency state`);
      this.lastServerSeqApplied = 0;
      this.processedEventIds.clear();
      this.lastEvaAudioSeq = 0;
      this.lastClientAudioSeq = 0;
      this.lastAudioSeq = 0;
      this.clientAudioSeq = 0;
      this.activeAssistantTurnId = undefined;
      this.pendingPlaybackAck = false;
      this.playbackAckSentForTurnId = undefined;
      this.setMicOpen(false);
      this.lastReconnectDelayMs = 0;
      this.reconnectAttempts = 0;
      this.rumMetrics.reset();
      this.reconnectJitterSeed = this.seedFromSessionId(newSessionId);
      this.protocolNegotiator.resetEpochTracking();
      this.ackCache.clear();
      if (this.lastTelemetryRollup) {
        this.lastTelemetryRollup = {};
      }
      this.currentSessionId = newSessionId;
    }

    // FE_CHANGES §4 — limpiar tracking de provider_epoch al iniciar sesión.
    // Reconnects (replay path) NO limpian: queremos detectar voice-client fresh
    // entre eventos en la misma question. Pero una nueva connect() lógica sí.
    // Ola 3 — calcular el flag de barge-in UNA SOLA VEZ al conectar.
    // localStorage override gana sobre config.json (para QA sin redeploy).
    this.bargeInEnabled = this.configService.bargeInEnabled;
    this.echoSuspectCount = 0;
    console.log(`[FC-SERVICE] bargeInEnabled=${this.bargeInEnabled}`);

    this.setState('connecting');

    try {
      let wsUrlString: string;

      if (this.isLocal && !this.configService.forceRemoteAgentEnabled) {
        // Desarrollo local: conectar directo al agente
        const url = new URL('ws://localhost:8085/ws');
        if (config.testId) url.searchParams.set('testId', config.testId);
        if (config.candidateEmail) url.searchParams.set('email', config.candidateEmail);
        if (config.sessionId) url.searchParams.set('sessionId', config.sessionId);
        wsUrlString = url.toString();
      } else {
        // Producción: obtener URL presignada de AgentCore
        const presignUrl = this.configService.urlConversationalPresign;
        const presignBody: Record<string, unknown> = {
          testId: config.testId,
          knowledgeId: '__fc__',
          questionIndex: 0,
          modality: 'full_conversation',
          email: config.candidateEmail,
        };
        if (config.sessionId) {
          presignBody['sessionId'] = config.sessionId;
        }
        const response = await firstValueFrom(
          this.http.post<{ presignedUrl: string }>(presignUrl, presignBody)
        );
        wsUrlString = response.presignedUrl;
      }

      console.log('[FC-SERVICE] Connecting to:', this.redactWsUrl(wsUrlString));
      // F1 — desenganchar handlers del socket viejo, y recién después bumpear el
      // epoch ANTES de crear el socket nuevo. Sin esto un onclose tardío del
      // socket anterior dispara un reconnect espurio sobre la conexión nueva.
      if (this.ws) {
        this.ws.onopen = null;
        this.ws.onmessage = null;
        this.ws.onerror = null;
        this.ws.onclose = null;
      }
      this.connectionEpoch++;
      const epoch = this.connectionEpoch;
      this.ws = new WebSocket(wsUrlString);
      this.ws.binaryType = 'arraybuffer';
      this.setupWebSocketHandlers(config, epoch);
    } catch (error) {
      console.error('[FC-SERVICE] Error connecting:', error);
      this.setState('error');
      this.commandSubject.next({ type: 'ERROR', message: 'Failed to connect' });
    }
  }
  
  /**
   * quality_mode=low path — no WebSocket / AgentCore / AJV envelope.
   * FcLowRuntime translates fc-low API + TTS into the same AgentCommand stream.
   */
  private async connectLow(config: FcSessionConfig): Promise<void> {
    this.qualityMode = 'low';
    this.metricsQualityMode = 'low';
    this.terminatedByEnd = false;
    this.finalizingWsClose = false;

    if (this.lowRuntime) {
      this.lowRuntime.stop();
      this.lowRuntime = null;
    }

    const newSessionId = config.sessionId;
    if (this.currentSessionId !== newSessionId) {
      this.lastServerSeqApplied = 0;
      this.processedEventIds.clear();
      this.activeAssistantTurnId = undefined;
      this.pendingPlaybackAck = false;
      this.playbackAckSentForTurnId = undefined;
      this.setMicOpen(false);
      this.rumMetrics.reset();
      this.currentSessionId = newSessionId;
    }

    this.setState('connecting');
    console.log('[FC-SERVICE] connectLow — fc-low/TTS runtime (no WS)');

    // CRITICAL: fc-low HTTP + browser TTS resolve outside Angular's zone.
    // Without ngZone.run, processCommand mutates component state (timers,
    // thread, silence bar) but the template never refreshes — shell mounts
    // empty (no phase-timer / silence-frame). High path stays in-zone via WS.
    const host = {
      ingestCommand: (command: AgentCommand): boolean => {
        return this.ngZone.run(() => {
          const normalized = this.normalizeLowCommand(command);
          if (normalized.type === 'ERROR') {
            this.setState('error');
            this.commandSubject.next(normalized);
            return true;
          }
          return this.processCommand(normalized);
        });
      },
      notifyPlaybackEnd: (): void => {
        this.ngZone.run(() => this.notifyPlaybackEnd());
      },
      setLocalAudioPlaying: (playing: boolean): void => {
        this.ngZone.run(() => this.notifyLocalAudioPlayingState(playing));
      },
      setAgentState: (state: string): void => {
        this.ngZone.run(() => this.setState(state as AgentState));
      },
      getAdaptationProfile: (): string | null => {
        try {
          return this.adaptationProfileProvider?.() ?? null;
        } catch {
          return null;
        }
      },
      shouldSuppressFollowupNarration: (): boolean => {
        try {
          return this.followupSuppressionProvider?.() ?? false;
        } catch {
          return false;
        }
      },
      getAffectSnapshot: (): AffectSnapshot | null => {
        try {
          return this.affectSnapshotProvider?.() ?? null;
        } catch {
          return null;
        }
      },
      reportMetric: (name: string, value: number, unit?: 'ms' | 'count', extra?: Record<string, string | number | boolean>): void =>
        this.sendClientMetric(name, value, unit, extra),
      getAnswerTimer: (): FcLowAnswerTimer | null => this.answerTimerProvider?.() ?? null,
    };

    this.lowRuntime = new FcLowRuntime(host, {
      lowApi: this.fcLow,
      tts: this.fcBrowserTts,
      vcScorer: this.vcScorer,
    });

    this.setState('initializing');
    try {
      await this.lowRuntime.start({
        testId: config.testId,
        email: config.candidateEmail,
        sessionId: config.sessionId,
      });
    } catch (error) {
      console.error('[FC-SERVICE] connectLow failed:', error);
      this.ngZone.run(() => {
        this.setState('error');
        this.commandSubject.next({
          type: 'ERROR',
          message: 'Failed to start low-quality session',
        });
      });
    }
  }

  /**
   * Map low-runtime synthetic commands onto the same field names the
   * fc-v5 adapter would produce from the wire (text, areaName, questionIndex).
   * processCommand + the component expect those aliases.
   */
  private normalizeLowCommand(command: AgentCommand): AgentCommand {
    const adapted = adaptFcV5Command(command) as AgentCommand & Record<string, unknown>;
    const raw = command as AgentCommand & Record<string, unknown>;

    if (!adapted['text'] && typeof raw['question_text'] === 'string') {
      adapted['text'] = raw['question_text'];
    }
    if (!adapted['text'] && typeof raw['display_text'] === 'string') {
      adapted['text'] = raw['display_text'];
    }
    if (adapted['questionIndex'] === undefined) {
      const idx = raw['question_index'] ?? raw['question_idx'] ?? raw['questionIndex'];
      if (typeof idx === 'number') {
        adapted['questionIndex'] = idx;
        adapted['question_idx'] = idx;
      }
    }
    if (!adapted['areaName']) {
      const area = raw['knowledge_name'] ?? raw['areaName'] ?? raw['knowledge'];
      if (typeof area === 'string' && area.length > 0) {
        adapted['areaName'] = area;
      }
    }
    // Preserve timer budgets / silence (adapter may not copy them on non-wire payloads).
    if (adapted['thinking_time_s'] === undefined && typeof raw['thinking_time_s'] === 'number') {
      adapted['thinking_time_s'] = raw['thinking_time_s'];
    }
    if (adapted['answer_budget_s'] === undefined && typeof raw['answer_budget_s'] === 'number') {
      adapted['answer_budget_s'] = raw['answer_budget_s'];
    }
    if (adapted['turn_silence_s'] === undefined && typeof raw['turn_silence_s'] === 'number') {
      adapted['turn_silence_s'] = raw['turn_silence_s'];
    }
    if (!adapted['turn_kind'] && typeof raw['turn_kind'] === 'string') {
      adapted['turn_kind'] = raw['turn_kind'];
    }
    return adapted as AgentCommand;
  }

  private setupWebSocketHandlers(config: FcSessionConfig, epoch: number = this.connectionEpoch): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      // F1 — epoch guard: ignorar si un socket más nuevo ya nos reemplazó.
      if (epoch !== this.connectionEpoch) return;
      console.log('[FC-SERVICE] WebSocket connected');
      this.audioHandshakeReady = false;
      this.clientAudioSeqHeaderActive = false;
      this.clientAudioMagicHeaderActive = false;
      this.setMicOpen(false);
      this.setState('initializing');
      
      const clientReloadDetected = config.clientReloadDetected === true && !this.reloadSignalSent;
      if (clientReloadDetected) {
        this.reloadSignalSent = true;
      }
      this.sendMessage({
        type: 'session_start',
        protocol: FC_V5_PROTOCOL,
        test_id: config.testId,
        candidate_email: config.candidateEmail,
        session_id: config.sessionId,
        last_server_seq_applied: this.lastServerSeqApplied,
        last_attempt_id: this.lastServerAttemptId || null,
        client_capabilities: FC_V5_CLIENT_CAPABILITIES,
        client_reload_detected: clientReloadDetected,
      });
      this.armSessionAckWatchdog(config);
    };
    
    this.ws.onmessage = (event) => {
      // F1 — epoch guard
      if (epoch !== this.connectionEpoch) return;
      this.ngZone.run(() => {
        this.handleMessage(event.data);
      });
    };

    this.ws.onerror = (error) => {
      // F1 — epoch guard
      if (epoch !== this.connectionEpoch) return;
      console.error('[FC-SERVICE] WebSocket error:', error);
      this.ngZone.run(() => {
        this.setState('error');
        this.commandSubject.next({ type: 'ERROR', message: 'Connection error' });
      });
    };

    this.ws.onclose = (event) => {
      // F1 — epoch guard: un onclose stale debe ser no-op silencioso.
      if (epoch !== this.connectionEpoch) return;
      console.log(
        '[FC-SERVICE] WebSocket closed:',
        event.code,
        event.reason,
        'wasClean=', event.wasClean,
      );
      this.clearSessionAckWatchdog();
      // Fix 1 — la supresión outbound NO debe sobrevivir una reconexión: si el WS
      // se cierra mientras Eva estaba narrando, el flag quedaría armado indefinidamente
      // y el mic permanecería silenciado tras la reconexión. Liberar siempre aquí,
      // antes de cualquier lógica de reconexión, sea cierre intencional o no.
      this.releaseOutboundSuppression();
      // Fase 7 B.4 — intentional close via disconnect()/endSession(): skip all
      // reconnect logic and clear the flag.
      if (this.finalizingWsClose) {
        this.finalizingWsClose = false;
        return;
      }
      this.ngZone.run(() => {
        // Bug AG.3 — gate por estado del interview (no por wasClean).
        // Antes solo reintentaba si `!event.wasClean`, pero servers/proxies
        // pueden cerrar "limpio" mid-entrevista (signed URL expirada,
        // AgentCore timeout, browser pause). Si el interview seguía activo,
        // reintentar; el backend resuelve via load_question_idx.
        // Fase 7 B.3 — also gate on terminatedByEnd to prevent farewell-race
        // reconnect after INTERVIEW_COMPLETE drains audio then WS drops.
        const interviewActive = !this.terminatedByEnd && this.stateSubject.value !== 'completed';
        if (interviewActive && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.attemptReconnect(config);
        } else if (interviewActive) {
          this.setState('reconnecting');
          this.commandSubject.next({
            type: 'RECONNECT_FAILED',
            message: `No se pudo reconectar tras ${this.maxReconnectAttempts} intentos.`,
            reconnectable: true,
          });
        }
      });
    };
  }

  private seedFromSessionId(sessionId: string): number {
    let seed = 2166136261;
    for (let i = 0; i < sessionId.length; i++) {
      seed ^= sessionId.charCodeAt(i);
      seed = Math.imul(seed, 16777619);
    }
    return seed >>> 0 || 1;
  }

  private nextReconnectJitter(): number {
    this.reconnectJitterSeed = (
      Math.imul(1664525, this.reconnectJitterSeed) + 1013904223
    ) >>> 0;
    return this.reconnectJitterSeed / 0x100000000;
  }

  private computeReconnectDelay(): number {
    const base = this.reconnectBaseDelayMs;
    const upper = Math.min(
      this.reconnectMaxDelayMs,
      Math.max(base, this.lastReconnectDelayMs * 3),
    );
    const delay = Math.floor(base + this.nextReconnectJitter() * (upper - base));
    this.lastReconnectDelayMs = delay;
    return delay;
  }

  private attemptReconnect(config: FcSessionConfig): void {
    this.reconnectAttempts++;

    // Bug AG.3 — emitir comando UI a partir del attempt 3 para que el
    // candidato vea "Reconectando..." con número de intento. Attempts 1-2
    // se mantienen silenciosos.
    if (this.reconnectAttempts >= this.reconnectUiThreshold) {
      this.commandSubject.next({
        type: 'RECONNECTING',
        attempt: this.reconnectAttempts,
        max: this.maxReconnectAttempts,
      });
    }

    const delayMs = this.computeReconnectDelay();
    console.log(
      `[FC-SERVICE] Reconnecting... attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delayMs}ms`,
    );
    // FE-1 — guardar el handle para poder cancelar si disconnect() o connect()
    // llegan durante el delay, evitando que se dispare un connect() zombie.
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(config);
    }, delayMs);
  }

  /** Arma (o reinicia) el watchdog de transición: el front está esperando el
   *  próximo `assistant_turn_started`. Se limpia cuando ese frame llega, en un
   *  state_resync, o al terminar/desconectar. */
  private armTransitionWatchdog(): void {
    this.clearTransitionWatchdog();
    this.transitionWatchdogTimer = setTimeout(() => {
      this.transitionWatchdogTimer = null;
      const interviewActive = !this.terminatedByEnd
        && this.stateSubject.value !== 'completed'
        && !this.finalizingWsClose;
      if (!interviewActive) return;
      console.warn(
        '[FC-SERVICE] transition_watchdog — no llegó assistant_turn_started tras la transición; forzando reconexión (state_resync)',
      );
      try {
        this.sendClientMetric('transition_watchdog_reconnect', 1, 'count');
      } catch { /* best-effort */ }
      // Cierre NO intencional (sin finalizingWsClose) → onclose reconecta y el
      // backend re-emite state_resync con el índice autoritativo (= reload manual).
      if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
        this.ws.close(4002, 'transition_watchdog');
      }
    }, this.transitionWatchdogMs);
  }

  private clearTransitionWatchdog(): void {
    if (this.transitionWatchdogTimer) {
      clearTimeout(this.transitionWatchdogTimer);
      this.transitionWatchdogTimer = null;
    }
  }

  private armSessionAckWatchdog(config: FcSessionConfig): void {
    this.clearSessionAckWatchdog();
    this.sessionAckWatchdogTimer = setTimeout(() => {
      this.sessionAckWatchdogTimer = null;
      if (this.audioHandshakeReady || this.terminatedByEnd || this.finalizingWsClose) return;

      console.warn('[FC-SERVICE] session_ack_timeout — reconnecting before audio handshake');
      this.setState('reconnecting');
      this.commandSubject.next({
        type: 'RECONNECTING',
        attempt: this.reconnectAttempts + 1,
        max: this.maxReconnectAttempts,
      });

      if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
        this.ws.close(4000, 'session_ack_timeout');
      } else if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.attemptReconnect(config);
      } else {
        this.commandSubject.next({
          type: 'RECONNECT_FAILED',
          message: `No se pudo completar el handshake tras ${this.maxReconnectAttempts} intentos.`,
          reconnectable: true,
        });
      }
    }, this.sessionAckWatchdogMs);
  }

  private clearSessionAckWatchdog(): void {
    if (this.sessionAckWatchdogTimer) {
      clearTimeout(this.sessionAckWatchdogTimer);
      this.sessionAckWatchdogTimer = null;
    }
  }

  /**
   * Bug AG.3 — reintentar manualmente desde la UI tras agotarse los
   * reintentos automáticos. Resetea el contador y vuelve a intentar.
   */
  manualReconnect(config: FcSessionConfig): void {
    this.reconnectAttempts = 0;
    this.lastReconnectDelayMs = 0;
    this.connect(config);
  }

  private isDuplicateCommand(command: AgentCommand): boolean {
    if (command.event_id && this.processedEventIds.has(command.event_id)) {
      console.warn(
        '[FC-DUP] descartado por event_id type=', command.type,
        'event_id=', command.event_id,
        'server_seq=', command.server_seq,
        'lastServerSeqApplied=', this.lastServerSeqApplied,
      );
      return true;
    }
    const bySeq = (
      typeof command.server_seq === 'number' &&
      command.server_seq > 0 &&
      command.server_seq <= this.lastServerSeqApplied
    );
    if (bySeq) {
      console.warn(
        '[FC-DUP] descartado por server_seq type=', command.type,
        'server_seq=', command.server_seq,
        'lastServerSeqApplied=', this.lastServerSeqApplied,
      );
    }
    return bySeq;
  }

  private markCommandApplied(command: AgentCommand): void {
    if (command.event_id) {
      this.processedEventIds.add(command.event_id);
      if (this.processedEventIds.size > 256) {
        this.processedEventIds = new Set(Array.from(this.processedEventIds).slice(-128));
      }
    }
    if (typeof command.server_seq === 'number' && command.server_seq > this.lastServerSeqApplied) {
      this.lastServerSeqApplied = command.server_seq;
    }
    if (command.question_id) this.currentQuestionId = command.question_id;
    if (command.phase) this.currentBackendPhase = command.phase;
    // FE_CHANGES §4 — provider_epoch tracking: si cambió para la misma question
    // el voice client es fresh → reset buffer audio local + métrica.
    const epochChange = this.protocolNegotiator.trackProviderEpoch({
      provider_epoch: command.provider_epoch,
      question_id: command.question_id,
    });
    if (epochChange.changed) {
      console.warn(
        '[FC-SERVICE] provider_epoch changed (voice client reconnected fresh):',
        `${epochChange.previousEpoch} → ${epochChange.currentEpoch}`,
        'question_id=', epochChange.questionId,
      );
      this.stopAudioPlayback('immediate');
      this.clearResumeBuffer();
    }
  }

  /**
   * true si `type` requiere ser tratado como ack-critical en fc-v5.
   * Defensa en profundidad: si el BE setea `requires_ack` lo respetamos, pero
   * también ack-eamos los críticos por type para no perder señal.
   */
  private isCriticalType(type: string): boolean {
    return CRITICAL_ACK_TYPES_V5.includes(type);
  }

  /**
   * FE_CHANGES §5 — `client_ack` idempotente:
   * Si llega el mismo `event_id` dos veces (replay), respondemos el MISMO
   * payload cacheado. Si es la primera vez, lo construimos, lo cacheamos
   * y lo enviamos.
   */
  private ackCommand(command: AgentCommand, applied: boolean): void {
    const critical = this.isCriticalType(command.type);
    if (!command.requires_ack && !critical) return;
    if (this.ws?.readyState !== WebSocket.OPEN) return;

    // Replay del mismo event_id → mismo ack payload (idempotencia).
    //
    // Mantener payload estricto: el timestamp local de debug vive en
    // `AckCacheEntry.debugTs` (no se serializa).
    const cached = command.event_id ? this.ackCache.getOrUndefined(command.event_id) : undefined;
    const payload: ClientAckPayload = cached ?? {
      type: 'client_ack',
      protocol: FC_V5_PROTOCOL,
      event_id: command.event_id,
      server_seq: command.server_seq,
      applied,
      client_phase: this.micOpenSubject.value ? 'mic_open' : (this.currentBackendPhase ?? this.currentNarrationPhase),
      local_audio_playing: this.localAudioIsPlaying,
      last_eva_audio_seq: this.lastEvaAudioSeq,
      last_client_audio_seq: this.lastClientAudioSeq,
    };
    if (!cached && command.event_id) {
      this.ackCache.store(command.event_id, payload);
    }
    console.info(
      `[FC-PROTOCOL] client_ack sent event_id=${command.event_id} server_seq=${command.server_seq} cached=${!!cached} applied=${applied}`,
    );
    this.ws.send(JSON.stringify(payload));
    this.sendClientMetric('client_ack_sent', 1, 'count');
  }
  
  private handleMessage(data: any): void {
    // Manejar datos binarios (audio)
    if (data instanceof Blob || data instanceof ArrayBuffer) {
      // Fase 8 B.1 — strip 4-byte seq header when BE echoed the capability.
      // NEVER strip without echo — garbled audio if BE doesn't prepend.
      if (data instanceof ArrayBuffer && this.serverAudioSeqHeaderActive && data.byteLength >= 6) {
        const view = new DataView(data);
        const seq = view.getUint32(0, false); // big-endian
        const pcm = data.slice(4);
        this.lastEvaAudioSeq = seq;
        // Update seq observability counters in __fcDebug.
        const dbg = (window as any).__fcDebug;
        if (dbg?.enabled) {
          const prev = dbg.lastEvaAudioSeqStored;
          dbg.lastEvaAudioSeq = seq;
          if (prev != null && seq !== 0 && seq !== prev + 1) {
            const gapSize = seq - prev;
            // Causa C fix: drop-oldest bajo backpressure genera gaps pequeños
            // (1-3 frames) que son benignos — el audio suena con artefactos
            // mínimos. Solo contar gaps grandes (>3) como incidente real.
            if (gapSize > 3) {
              dbg.evaChunkGaps = (dbg.evaChunkGaps || 0) + 1;
            } else {
              dbg.evaChunkGapsSmall = (dbg.evaChunkGapsSmall || 0) + 1;
            }
          }
          // seq === 0 after prev > 0 → turn boundary, no gap.
          dbg.lastEvaAudioSeqStored = seq;
        }
        this.playAudio(pcm);
        return;
      }
      this.playAudio(data);
      return;
    }

    // Manejar texto (JSON)
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);

        // FE-3 — validar que el mensaje tiene shape mínimo de envelope antes
        // de procesarlo. Solo descartamos payloads sin `type` string — el
        // `protocol` es opcional (BE no lo inyecta en todos los mensajes).
        // Para mensajes que SÍ traen `protocol`, verificamos que sea v5.
        // asV5Envelope solo valida que protocol === 'fc-v5'; lo usamos cuando
        // el campo existe para detectar protocol mismatch inesperado.
        if (!parsed || typeof parsed !== 'object' || typeof parsed.type !== 'string') {
          console.warn('[FC-PROTOCOL] dropped invalid envelope (no type):', parsed);
          return;
        }
        if (parsed.protocol !== undefined) {
          const envelope = this.protocolNegotiator.asV5Envelope(parsed);
          if (!envelope) {
            console.warn('[FC-PROTOCOL] dropped envelope with unknown protocol:', parsed.protocol, parsed);
            return;
          }
        }

        // AJV strict envelope validation. Once initialized, malformed fc-v5
        // messages are dropped fail-closed instead of being adapted loosely.
        if (this.envelopeValidator && !this.envelopeValidator(parsed)) {
          console.error('[FC-PROTOCOL] schema_violation', this.envelopeValidator.errors, parsed);
          try {
            this.sendClientMetric('schema_violation', 1, 'count');
          } catch {}
          return;
        }

        const command: AgentCommand = adaptFcV5Command(parsed);
        // FE_CHANGES §4 — log de server_seq + protocol envelope para debugging.
        if (typeof command.server_seq === 'number' && command.type !== 'ping') {
          console.debug(
            '[FC-SERVICE] envelope',
            'type=', command.type,
            'server_seq=', command.server_seq,
            'protocol=', command.protocol,
            'requires_ack=', command.requires_ack,
            'provider_epoch=', command.provider_epoch,
          );
        }
        // Bug AG.2 — el backend manda `{"type":"ping","ts":<unix>}` cada
        // 25s. NO emitimos al usuario; solo refrescamos un timestamp
        // local para futuras detecciones de zombie connections.
        if ((command as any).type === 'ping') {
          this.lastServerActivityTs = Date.now();
          return;
        }
        if (this.isDuplicateCommand(command)) {
          this.ackCommand(command, true);
          return;
        }
        // Fase 1 — backend notifica ausencia de capture_heartbeat.
        if (command.type === 'capture_stalled') {
          this.captureStalledSubject.next();
          this.markCommandApplied(command);
          this.ackCommand(command, true);
          return;
        }
        // Fase 8 B.1 — detect server_capabilities echo from BE.
        // BE sends this in the session_start ack (or a dedicated command).
        if ((command as any).server_capabilities?.audio_seq_header === true) {
          this.serverAudioSeqHeaderActive = true;
          console.log('[FC-SERVICE] server_capabilities echo: audio_seq_header=true');
        } else if ((command as any).server_capabilities?.audio_seq_header === false) {
          this.serverAudioSeqHeaderActive = false;
        }
        const applied = this.processCommand(command);
        if (applied) {
          this.markCommandApplied(command);
        }
        this.ackCommand(command, applied);
      } catch (error) {
        console.error('[FC-SERVICE] Error parsing message:', error);
      }
    }
  }
  
  private processCommand(command: AgentCommand): boolean {
    // Log universal de comando recibido para trazabilidad en CloudWatch+DevTools.
    // Solo el tipo, no payload completo, para no inundar la consola con audio b64.
    // Log universal de comando recibido para trazabilidad en CloudWatch+DevTools.
    // Coalescado: el hot-path WS generaba decenas de miles de líneas por sesión.
    coalesceLog(`FC:cmd:${command.type}`, '[FC-SERVICE] Command received:', command.type);

    switch (command.type) {
      case 'session_ack': {
        console.log('[FC-SERVICE] session_ack received (protocol=fc-v5)');
        this.clearSessionAckWatchdog();
        this.reconnectAttempts = 0;
        this.lastReconnectDelayMs = 0;
        const seqHeader = command.server_capabilities?.audio_seq_header;
        if (seqHeader !== undefined) {
          this.serverAudioSeqHeaderActive = seqHeader === true;
        }
        const clientSeqHeader = command.server_capabilities?.client_audio_seq_header;
        if (clientSeqHeader !== undefined) {
          this.clientAudioSeqHeaderActive = clientSeqHeader === true;
        }
        const clientMagicHeader = command.server_capabilities?.client_audio_magic_header;
        this.clientAudioMagicHeaderActive = clientMagicHeader === true;
        this.audioHandshakeReady = true;
        if (typeof command.reload_count === 'number') {
          this.reloadCount = command.reload_count;
        }
        if (typeof command.reload_max === 'number') {
          this.reloadMax = command.reload_max;
        }
        if (typeof command.reload_limit_reached === 'boolean') {
          this.reloadLimitReached = command.reload_limit_reached;
        }
        console.log('[FC-RELOAD] session_ack reload_count=', this.reloadCount, 'reload_max=', this.reloadMax, 'reload_limit_reached=', this.reloadLimitReached);
        if (typeof command.question_idx === 'number') {
          this.progressSubject.next({
            ...this.progressSubject.value,
            current: command.question_idx + 1,
          });
          this.lastProcessedQuestionIdx = command.question_idx;
        }
        if (command.question_id) {
          this.currentQuestionId = command.question_id;
        }
        if (command.attempt_id) {
          this.lastServerAttemptId = command.attempt_id;
        }
        if (typeof command.questionIndex === 'number') {
          this.currentQuestionIndex = command.questionIndex;
        }
        this.setMicOpen(command.mic_open === true);
        this.currentBackendPhase = command.phase;
        if (command.phase === 'question') {
          this.currentNarrationPhase = 'question';
        } else if (command.phase === 'farewell') {
          this.currentNarrationPhase = 'farewell';
        } else {
          this.currentNarrationPhase = 'greeting';
        }
        break;
      }

      case 'state_resync': {
        // Recuperado (reconexión, incluida la que fuerza el transition_watchdog).
        this.clearTransitionWatchdog();
        this.stopAudioPlayback('reset');
        this.clearResumeBuffer();
        this.releaseOutboundSuppression();
        if (typeof command.question_idx === 'number') {
          this.progressSubject.next({
            ...this.progressSubject.value,
            current: command.question_idx + 1,
          });
          this.lastProcessedQuestionIdx = command.question_idx;
          this.currentQuestionIndex = command.question_idx;
        }
        if (command.question_id) this.currentQuestionId = command.question_id;
        if (command.assistant_turn_id) this.activeAssistantTurnId = command.assistant_turn_id;
        if (typeof command.audio_seq_end === 'number') this.lastEvaAudioSeq = command.audio_seq_end;
        if (command.areaName) this.areaSubject.next(command.areaName);
        if (typeof command.total_questions === 'number') {
          this.progressSubject.next({
            current: this.progressSubject.value.current,
            total: command.total_questions,
          });
        }
        if (command.phase) this.currentBackendPhase = command.phase;
        this.setMicOpen(command.mic_open === true);
        // Fix E (state_resync clear_audio_buffer) — el BE emite este flag
        // cuando hace retry de la sesión S2S pre-narración. Antes mandaba
        // 'interrupted', pero eso disparaba el gate anti-eco (false barge-in
        // metric) y stopAllAudio innecesario. Acá hacemos limpieza directa
        // del buffer de audio sin propagar barge-in semantics.
        if (command.clear_audio_buffer === true) {
          console.log('[FC-SERVICE] state_resync clear_audio_buffer=true — stopping audio (no barge-in)');
          if ((window as any).__fcDebug?.enabled) {
            (window as any).__fcDebug.stateResyncClearAudio =
              ((window as any).__fcDebug.stateResyncClearAudio || 0) + 1;
          }
          this.stopAudioPlayback('reset');
        }
        break;
      }

      case 'assistant_turn_started': {
        // Llegó la narración de la próxima pregunta → el front dejó de esperar.
        this.clearTransitionWatchdog();
        if (command.display_text) {
          console.log('[Eva]', command.display_text);
          // Bug 8 fix (ARCHITECTURE_V5_CURRENT_STATE.md §7): the transcript
          // panel used to be fed ONLY by 'transcription' events, which the
          // backend never emits in fc-v5 (dead code path — OutTranscription
          // is defined/exported but never constructed, see §6). Every
          // narration whose audio came back muted or cut short left the
          // panel blank even though display_text had the text all along.
          const isEchoTrigger = command.display_text.trim().toLowerCase().startsWith('(sistema');
          const isDuplicate = !!command.display_revision
            && command.display_revision === this.lastAgentTranscriptRevision;
          if (!isEchoTrigger && !isDuplicate) {
            this.lastAgentDisplayText = command.display_text;
            this.lastAgentTranscriptRevision = command.display_revision;
            this.transcriptSubject.next({ role: 'agent', text: command.display_text, mode: 'replace' });
          }
        }
        this.activeAssistantTurnId = command.assistant_turn_id ?? command.assistantTurnId;
        this.playbackAckSentForTurnId = undefined;
        this.pendingPlaybackAck = false;
        this.activeAssistantTurnStartedAtMs = Date.now();
        this.activeAssistantLogicalCompleteAtMs = 0;
        this.lastEvaAudioSeq = typeof command.audio_seq_start === 'number'
          ? Math.max(0, command.audio_seq_start)
          : 0;
        if (command.question_id) this.currentQuestionId = command.question_id;
        if (typeof command.questionIndex === 'number') this.currentQuestionIndex = command.questionIndex;
        this.currentBackendPhase = command.phase ?? 'assistant_streaming';
        this.currentNarrationPhase = command.phase === 'farewell'
          ? 'farewell'
          : command.phase === 'greeting' || command.phase === 'intro'
            ? 'greeting'
            : 'question';
        this.setMicOpen(false);
        this.setState('narrating');
        this.resetEvaMetricsTurn();
        if (!this.bargeInEnabled) {
          this.suppressOutboundUntilPlaybackReady('narration');
        }
        break;
      }

      case 'audio_gap_detected':
        console.warn('[FC-SERVICE] audio_gap_detected from backend', command);
        this.sendClientMetric('audio_gap_detected', 1, 'count');
        break;

      case 'next_question': {
        // fc-v5 usa `question_index`; el adaptador normaliza a `questionIndex`.
        if (command.questionIndex === undefined && command.question_idx !== undefined) {
          command.questionIndex = command.question_idx;
        }
        if (command.assistant_turn_id) this.activeAssistantTurnId = command.assistant_turn_id;
        if (command.question_id) this.currentQuestionId = command.question_id;
        if (typeof command.questionIndex === 'number') this.currentQuestionIndex = command.questionIndex;
        if (typeof command.total_questions === 'number') {
          this.progressSubject.next({
            current: this.progressSubject.value.current,
            total: command.total_questions,
          });
        }
        console.log(
          '[FC-SERVICE] Next question, idx=',
          command.questionIndex,
          'area=',
          command.areaName,
        );
        if (command.areaName) this.areaSubject.next(command.areaName);
        // Ola 0 — a partir de aquí la narración activa es una pregunta normal.
        this.currentNarrationPhase = 'question';
        // LOW compromete 'narrating' antes del puente de avance: esa voz ya
        // se oye. Bajar a 'transitioning' pintaría "pensando" y el overlay
        // entre el puente y la pregunta. En high el estado acá no es narrating
        // (el hueco antes del audio sigue siendo transitioning).
        if (this.stateSubject.value !== 'narrating') {
          this.setState('transitioning');
        }
        // El front ahora espera el assistant_turn_started de la próxima
        // pregunta; si no llega, el watchdog fuerza reconexión (state_resync).
        this.armTransitionWatchdog();
        // Fix RE-NARRACIÓN — si el idx es el mismo que el último procesado,
        // es un reinicio silencioso del BE (Gemini session restart). No resetear
        // budgets de falsa interrupción ni dead-air: la re-narración no es pregunta nueva.
        const isRenarration =
          command.questionIndex !== undefined &&
          command.questionIndex === this.lastProcessedQuestionIdx;
        if (!isRenarration) {
          // FC Feature 2 — nueva pregunta: descartar buffer + resetear budget.
          this.clearResumeBuffer();
          this.falseInterruptBudget = 1;
          if (command.questionIndex !== undefined) {
            this.lastProcessedQuestionIdx = command.questionIndex;
            this.progressSubject.next({
              ...this.progressSubject.value,
              current: command.questionIndex + 1,
            });
          }
        }
        // [FC] Defecto 1 & 2 — NO limpiar el buffer de transcripción aquí.
        // Antes emitíamos text='' para señalizar limpieza, pero eso borraba los
        // transcripts de la pregunta anterior ANTES de que la nueva pregunta
        // apareciera en pantalla (el swap se defería hasta el play del audio).
        // Ahora la limpieza ocurre en applyNextQuestionCommand() del componente,
        // sincronizada con el momento exacto del cambio de display.
        break;
      }

      case 'assistant_logical_complete': {
        if (command.assistant_turn_id) this.activeAssistantTurnId = command.assistant_turn_id;
        if (command.question_id) this.currentQuestionId = command.question_id;
        if (typeof command.questionIndex === 'number') this.currentQuestionIndex = command.questionIndex;
        if (typeof command.audio_seq_end === 'number') {
          this.lastEvaAudioSeq = Math.max(this.lastEvaAudioSeq, command.audio_seq_end);
        }
        this.activeAssistantLogicalCompleteAtMs = Date.now();
        this.pendingPlaybackAck = command.requires_playback_ack !== false;
        this.currentBackendPhase = 'playback_draining';
        this.setMicOpen(false);
        this.setState('draining_audio');
        if (this.pendingPlaybackAck) {
          const buffersDrained = this.audioBuffersDrainedProvider
            ? this.audioBuffersDrainedProvider()
            : !this.localAudioIsPlaying;
          if (buffersDrained && !this.localAudioIsPlaying) {
            this.sendPlaybackAck('playback_ended');
          } else {
            this.releaseOnPlaybackEnd = true;
            this.armPlaybackEndDeadline();
          }
        }
        break;
      }

      case 'mic_open':
        if (command.assistant_turn_id) this.activeAssistantTurnId = command.assistant_turn_id;
        if (command.question_id) this.currentQuestionId = command.question_id;
        if (typeof command.questionIndex === 'number') this.currentQuestionIndex = command.questionIndex;
        // Arm timers from mic_open budgets when there was no next_question
        // (greeting / followup). Must stash BEFORE setMicOpen — micOpen$ is sync.
        {
          const hasThink = typeof command.thinking_time_s === 'number';
          const hasAnswer = typeof command.answer_budget_s === 'number';
          if (hasThink || hasAnswer) {
            const rawAnswer = hasAnswer ? command.answer_budget_s! : 0;
            this.pendingMicOpenTimerArm = {
              thinkingTimeS: hasThink ? command.thinking_time_s! : 0,
              answerBudgetS: rawAnswer > 0 ? rawAnswer : 90,
              questionIdx:
                typeof command.questionIndex === 'number'
                  ? command.questionIndex
                  : this.currentQuestionIndex,
              // Salvavidas (LOW): la pista re-abre la MISMA pregunta con una
              // ventana nueva — solo se agrega la key cuando aplica.
              // Módulo de experiencia previa: fresh_window (cada turno intro
              // trae su propio think/answer; nunca hereda el remanente del saludo).
              ...(command.reason === 'lifeline_hint' || command.fresh_window === true
                ? { freshWindow: true }
                : {}),
              ...(command.confirm_advance === true ? { confirmAdvance: true } : {}),
              ...pauseArm(command.pause_s),
            };
          }
        }
        this.currentBackendPhase = 'candidate_listening';
        this.releaseOutboundSuppression();
        this.setState('listening');
        this.setMicOpen(true);
        break;

      case 'user_turn_complete':
        if (command.transcript) {
          console.log('[Candidato]', command.transcript);
        }
        // logs5 hard rule: do NOT close the mic here. The candidate often keeps
        // talking after a short complete fragment; if we stop PCM the agent goes
        // deaf and Eva talks over them. Keep sending audio for observation until
        // assistant_turn_started (which closes the mic before Eva speaks).
        this.currentBackendPhase = 'waiting_model';
        this.setState('processing');
        this.resetEvaMetricsTurn();
        break;

      case 'interview_complete':
        // VC detection (CONTRATO-VC-DETECTION.md §4.3) — termination_reason
        // viaja solo para trazabilidad acá; el ruteo fino de pantalla
        // (suspendida vs error genérico) lo hace el componente mirando este
        // mismo campo directo del command (ver handleCommand/case
        // 'interview_complete' en fc-session.component.ts). El AgentState
        // sigue siendo el genérico 'error' para CUALQUIER reason==='aborted'
        // — no se agrega un estado nuevo solo para esto (nadie más lo consume).
        console.log('[FC-SERVICE] Interview complete', {
          reason: command.reason,
          termination_reason: command.termination_reason,
        });
        // Ola 0 — la narración que va a sonar es el farewell (clean ends only).
        this.currentNarrationPhase = 'farewell';
        // Fase 7 B.3 — lock out reconnect before transitioning to completed.
        this.terminatedByEnd = true;
        this.setMicOpen(false);
        // P1-4: anomalous teardown → do not advertise agentState=completed
        // (component routes reason=aborted to viewState=error).
        if (command.reason === 'aborted') {
          this.setState('error');
        } else {
          this.setState('completed');
        }
        break;

      case 'session_expired':
        console.warn('[FC-SERVICE] Session expired');
        this.terminatedByEnd = true;
        this.setState('completed');
        this.setMicOpen(false);
        break;

      case 'transcription':
        // Bug 8 finding (ARCHITECTURE_V5_CURRENT_STATE.md §6): the backend
        // never constructs OutTranscription in fc-v5 today — this case is
        // effectively dead code for the current wire traffic. Left in place
        // (not removed) because its role==='user' branch below has real
        // side effects (onUserSpeechDetected, clearResumeBuffer) that would
        // need to be re-wired if the backend ever starts emitting this.
        if (command.text && command.role) {
          // [ECHO-TRIGGER] cinturón FE (incidente 13ade11a): jamás mostrar
          // texto de instrucciones internas en el panel del candidato.
          if (
            command.role !== 'user' &&
            command.text.trim().toLowerCase().startsWith('(sistema')
          ) {
            console.warn('[FC-SERVICE] transcripción de trigger interno filtrada');
            break;
          }
          // Truncamos a 120 chars para no saturar la consola con párrafos.
          const preview = command.text.length > 120
            ? `${command.text.slice(0, 120)}…`
            : command.text;
          coalesceLogWith(
            `FC:tx:${command.role}`,
            { windowMs: 1500 },
            `[FC-SERVICE] Transcription [${command.role}]:`,
            preview,
          );
          this.transcriptSubject.next({ role: command.role, text: command.text });
          if (command.role === 'user') {
            // FC Feature 2 — habla real reconocida: interrupción REAL → descartar buffer.
            if (this.falseInterruptTimer !== null) {
              this.falseInterruptSawUserTranscript = true;
              this.clearResumeBuffer();
            }
            this.onUserSpeechDetected();
          } else if (
            command.role === 'agent'
            && this.stateSubject.value === 'transitioning'
          ) {
            // Eva ya empezó a narrar la siguiente pregunta — el overlay
            // "Pasando a la siguiente pregunta..." debe desaparecer aunque
            // todavía no llegue assistant_logical_complete (el audio puede durar varios
            // segundos). Sin esto el spinner queda colgado encima del texto
            // que Eva está leyendo en voz alta. El estado 'narrating' es
            // intermedio y solo lo lee el HTML para condicionar el overlay.
            this.setState('narrating');
          }
        }
        break;

      case 'interrupted': {
        // Anti-eco: si Eva empezó a narrar hace < 4s, este "interrupted"
        // es casi seguramente eco acústico (su audio volvió por el mic) — ignorar
        // completamente (no propagamos al componente tampoco para evitar stopAllAudio).
        const narrationStart = this.narrationStartTs;
        if (narrationStart !== null) {
          const ageMs = performance.now() - narrationStart;
          // Gate de 1.5s cuando barge-in habilitado (FE declaró capability); 4s conservador.
          const gateMs = this.bargeInEnabled ? 1500 : 4000;
          if (ageMs < gateMs) {
            console.log('[FC-SERVICE] Barge-in IGNORED — narration too recent', ageMs.toFixed(0), 'ms (gate=' + gateMs + 'ms)');
            if ((window as any).__fcDebug?.enabled) {
              (window as any).__fcDebug.bargeinIgnoredTooEarly =
                ((window as any).__fcDebug.bargeinIgnoredTooEarly || 0) + 1;
            }
            return true;  // no emitir al componente, pero el comando sí fue manejado
          }
        }
        // Fix E (P1 3.2) — min-speech gate INCONDICIONAL: solo aceptar 'interrupted'
        // si hubo voz local sostenida. Previamente envuelto en if (bargeInEnabled),
        // lo que dejaba pasar TODOS los false-interrupted en producción (flag off).
        // El gate ahora corre siempre; el auto-disable de barge-in solo aplica si
        // el flag estaba on (no-op cuando ya estaba off).
        const speechMs = this.lastLocalSpeechWindowMs;
        if (speechMs < 300) {
          this.echoSuspectCount++;
          console.warn('[FC-SERVICE] Barge-in REJECTED — no local speech (speechMs=' + speechMs + ', echoSuspect=' + this.echoSuspectCount + ', bargeIn=' + this.bargeInEnabled + ')');
          if ((window as any).__fcDebug?.enabled) {
            (window as any).__fcDebug.interruptedRejectedNoLocalSpeech =
              ((window as any).__fcDebug.interruptedRejectedNoLocalSpeech || 0) + 1;
          }
          // Auto-protección: ≥3 sospechas de eco → deshabilitar barge-in en runtime.
          // Solo emite la métrica/transición si bargeInEnabled estaba on (evita ruido
          // en producción donde el flag ya está off y queda como simple guardia).
          if (this.echoSuspectCount >= 3 && this.bargeInEnabled) {
            console.warn('[FC-SERVICE] Auto-disabling barge-in (echoSuspectCount >= 3)');
            this.bargeInEnabled = false;
            // Re-armar supresión para el próximo turno (el actual ya lo ignoramos).
            this.outboundSuppressArmed_narration = false;
            this.sendClientMetric('barge_in_auto_disabled', 1, 'count');
          }
          return true;  // ignorar — no propagar al componente
        }
        console.log('[FC-SERVICE] Barge-in: user interrupted Eva');
        // FC Feature 2 — en vez de descartar la cola directamente, primero
        // pedimos al componente que guarde los chunks no reproducidos en el
        // resumeBuffer ANTES de detener el playback. El componente llama
        // captureResumeBuffer() → luego stopAllAudio() por sí mismo.
        // Si no hay budget o el componente no respondió, el componente hace
        // stopAllAudio() de todas formas (comportamiento existente).
        this.falseInterruptSawUserTranscript = false;
        this.falseInterruptSawNewEvaChunk = false;
        this.stopAudioPlayback('immediate');
        // Ola 3 — reset funnel de métricas: nuevo turno de Eva comienza.
        this.resetEvaMetricsTurn();
        break;
      }

      case 'followup_requested':
        console.log(
          '[FC-SERVICE] followup_requested: missing=',
          command.missing_aspect,
          'count=',
          command.followup_count,
          'hasText=',
          !!command.text,
        );
        // Profundización dentro de la misma pregunta — no cambia idx ni
        // mata audio. El modelo va a hablar pidiendo ampliar.
        this.setState('follow_up');
        // Fix A (followup render) — hidratar text + phase ANTES de que llegue
        // el audio. Antes el text recién aparecía via transcript[assistant],
        // que llega tarde (después de los primeros chunks). Esto deja el
        // panel visible con la pregunta del followup desde el primer ms.
        // Bug 8 fix: assistant_turn_started (above) already hydrated the
        // panel with this same text via display_text — only re-emit if it
        // actually differs (defends whichever order the two events arrive
        // in; a no-op re-push of identical text is harmless either way).
        if (command.text && command.text !== this.lastAgentDisplayText) {
          this.lastAgentDisplayText = command.text;
          this.transcriptSubject.next({ role: 'agent', text: command.text, mode: 'replace' });
        }
        // La fase actual sigue siendo 'question' durante un followup (el BE
        // está profundizando sobre la misma pregunta del banco).
        this.currentNarrationPhase = command.phase === 'farewell'
          ? 'farewell'
          : command.phase === 'greeting' || command.phase === 'intro'
            ? 'greeting'
            : 'question';
        // NO armar supresión aquí. En fc-v5 el BE emite followup_requested
        // DESPUÉS de assistant_logical_complete + mic_open (question.py::_narrate
        // bloquea hasta el ack del mic antes de retornar) — la narración del
        // followup ya fue cubierta por el arm de 'assistant_turn_started' más
        // arriba. Armar de nuevo aquí re-cierra el mic sobre un turno que ya
        // terminó, y como no hay más chunks en camino la supresión solo se
        // libera con el mic_open del turno SIGUIENTE — bug confirmado en sesión
        // de prueba 2026-08-06 (session=46e76e4f…, 4/4 ventanas de followup).
        break;

      case 'criteria_eval':
        // DEBUG (dev-monitor): cómo la IA calificó cada criterio + por qué
        // decidió este turno. Solo llega en local/dev (gated en el BE). No
        // toca el flujo de la entrevista; solo alimenta el panel movible.
        this.criteriaEvalSubject.next({
          questionIdx: command.question_idx ?? 0,
          questionId: command.question_id ?? '',
          decisionKind: command.decision_kind ?? '',
          decisionReason: command.decision_reason ?? '',
          missingAspect: command.missing_aspect ?? '',
          followupText: command.followup_text ?? '',
          criteria: command.criteria ?? [],
          ...(typeof command.distress_score === 'number'
            ? { distressScore: command.distress_score }
            : {}),
          ...(command.lifeline_trigger !== undefined
            ? { lifelineTrigger: command.lifeline_trigger }
            : {}),
          at: Date.now(),
        });
        break;

      case 'ping':
        // Keep-alive del backend: no-op (BE no procesa pong).
        break;

      case 'timer_resync':
        // Sin efectos acá — el componente reanuda el cronómetro (ver
        // handleCommand en fc-session.component.ts).
        break;

      case 'error':
        console.error('[FC-SERVICE] Server error:', command.message ?? command.text);
        this.setMicOpen(false);
        this.setState('error');
        break;

      default: {
        // fc-cdf3bdad postmortem: comandos desconocidos no deben llegar al
        // commandSubject (el componente no sabe qué hacer con tipos inesperados).
        console.warn('[FC-SERVICE] unknown_command_type', {
          type: command?.type,
          server_seq: command?.server_seq,
        });
        try {
          this.sendClientMetric('unknown_command_type', 1, 'count');
        } catch {}
        return false; // sin emitir al commandSubject ni aplicar nada
      }
    }

    // Emitir el comando para que el componente lo maneje
    this.commandSubject.next(command);
    return true;
  }
  
  private setState(state: AgentState): void {
    this.stateSubject.next(state);
  }

  // Apaga el indicador de mic (REC + barra verde) SIN cortar el envío: el
  // audio en vuelo sigue mandándose para no perder el final de la transcripción.
  muteMicIndicator(): void {
    this.setMicOpen(false);
  }

  // Corta el envío de audio al agente hasta el próximo mic_open. Se llama tras
  // un breve flush (ver cutOffAndAdvance) para que el último chunk ya hablado
  // alcance a llegar antes de que el pass cierre el turno.
  suppressOutboundForAdvance(): void {
    this.suppressOutboundUntilPlaybackReady('manual');
  }

  private setMicOpen(isOpen: boolean): void {
    if (this.micOpenSubject.value !== isOpen) {
      this.micOpenSubject.next(isOpen);
    }
    // isRecording$ queda como señal de permiso lógico; la captura física
    // del worklet vive en el componente y puede seguir activa para mantener el path caliente.
    this.recordingSubject.next(isOpen);
    if (isOpen) {
      this.resetSilenceDetection();
    } else if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout);
      this.silenceTimeout = null;
    }
  }

  /**
   * Convert worklet float RMS (0..1) to Q15 u16 for the BE voice gate.
   * logs8 root cause: this field was always packed as 0 → BE never saw speech.
   */
  private floatRmsToQ15(rmsFloat: number): number {
    if (!Number.isFinite(rmsFloat) || rmsFloat <= 0) return 0;
    // Full-scale float ≈ 1.0 → 32767. Clamp to u16.
    return Math.max(0, Math.min(65535, Math.round(rmsFloat * 32767)));
  }

  private withClientAudioSeqHeader(audioData: ArrayBuffer | Blob): ArrayBuffer | Blob {
    if (!this.clientAudioSeqHeaderActive || !(audioData instanceof ArrayBuffer)) {
      return audioData;
    }
    const seq = this.clientAudioSeq >>> 0;
    this.clientAudioSeq = (this.clientAudioSeq + 1) >>> 0;
    this.lastClientAudioSeq = seq;
    this.lastAudioSeq = seq;
    const bytesPerSample = 2;
    const sampleRate = 16000;
    const durationMs = Math.max(
      1,
      Math.min(65535, Math.round((audioData.byteLength / bytesPerSample / sampleRate) * 1000)),
    );
    const rmsQ15 = this.floatRmsToQ15(this.lastOutboundRmsFloat);
    if (!this.clientAudioMagicHeaderActive) {
      const framed = new ArrayBuffer(audioData.byteLength + 8);
      const view = new DataView(framed);
      view.setUint32(0, seq, false);
      view.setUint16(4, durationMs, false);
      view.setUint16(6, rmsQ15, false);
      new Uint8Array(framed, 8).set(new Uint8Array(audioData));
      return framed;
    }
    const framed = new ArrayBuffer(audioData.byteLength + 12);
    const view = new DataView(framed);
    view.setUint32(0, this.clientAudioMagicHeader, false);
    view.setUint32(4, seq, false);
    view.setUint16(8, durationMs, false);
    view.setUint16(10, rmsQ15, false);
    new Uint8Array(framed, 12).set(new Uint8Array(audioData));
    return framed;
  }
  
  /**
   * Envía audio al agente — PURE firehose.
   * Solo ws.send + Fase 0 counters. Sin side-effects sobre estado de UI.
   * Llama a markUserSpeechActivity() por separado desde el componente
   * cuando el chunk representa actividad de voz real.
   *
   * @param rmsFloat Optional worklet RMS 0..1 for this chunk (logs8: required
   *   so BE soft/voiced clocks can detect fillers and mid-thought speech).
   */
  sendAudio(audioData: ArrayBuffer | Blob, rmsFloat?: number): boolean {
    if (typeof rmsFloat === 'number' && Number.isFinite(rmsFloat)) {
      this.lastOutboundRmsFloat = Math.max(0, rmsFloat);
    }
    if (this.ws?.readyState === WebSocket.OPEN) {
      if (!this.audioHandshakeReady) {
        if ((window as any).__fcDebug?.enabled) {
          (window as any).__fcDebug.audioChunksSuppressedOutbound =
            ((window as any).__fcDebug.audioChunksSuppressedOutbound || 0) + 1;
        }
        return false;
      }
      if (!this.micOpenSubject.value) {
        if ((window as any).__fcDebug?.enabled) {
          (window as any).__fcDebug.audioChunksSuppressedMicClosed =
            ((window as any).__fcDebug.audioChunksSuppressedMicClosed || 0) + 1;
        }
        return false;
      }
      // Fase 9.1 — suppress outbound audio during drain windows (greeting / transition / echo_tail)
      // and while narration suppression is armed until mic_open/playback_end.
      // Worklet keeps producing chunks so the audio path stays warm; we just skip the send.
      if (this.outboundSuppressArmed_narration || Date.now() < this.audioOutputSuppressedUntilMs) {
        this.audioOutputSuppressCount++;
        if ((window as any).__fcDebug?.enabled) {
          (window as any).__fcDebug.audioChunksSuppressedOutbound =
            ((window as any).__fcDebug.audioChunksSuppressedOutbound || 0) + 1;
        }
        return false;  // suppress send
      }
      // Fix 2 — guard contra desbordamiento del buffer TCP del WS: si hay >= 64KB
      // pendiente de envío el socket está congestionado; descartar el chunk para no
      // empeorar la situación ni aumentar la latencia del pipeline de Gemini.
      const bufferedAmount = this.ws.bufferedAmount;
      if (bufferedAmount >= 65536) {
        const now = Date.now();
        this.noteWsAudioBackpressure(now, bufferedAmount);
        if (now - this.lastWsSendBufferOverflowWarnMs >= 2000) {
          this.lastWsSendBufferOverflowWarnMs = now;
          console.warn('[FC] ws_send_buffer_overflow', { bufferedAmount });
          this.sendClientMetric('ws_audio_backpressure', 1, 'count');
        }
        if ((window as any).__fcDebug?.enabled) {
          (window as any).__fcDebug.wsSendBufferOverflows =
            ((window as any).__fcDebug.wsSendBufferOverflows || 0) + 1;
        }
        return false;
      }
      // [FC_DEBUG] Fase 0 diagnostic — capture readyState before send (per-chunk: counter only)
      const rs = this.ws.readyState;
      try {
        this.ws.send(this.withClientAudioSeqHeader(audioData));
        if ((window as any).__fcDebug?.enabled) {
          (window as any).__fcDebug.wsSendCalls++;
          (window as any).__fcDebug.wsReadyStateLastSend = rs;
        }
        return true;
      } catch (err) {
        if ((window as any).__fcDebug?.enabled) {
          (window as any).__fcDebug.wsSendErrors++;
          (window as any).__fcDebug.lastSendErrorMessage = String(err);
          console.error('[FC_DEBUG] ws_send_error', { err, readyState: rs, at: performance.now() });
        }
        console.warn('[FC-SERVICE] sendAudio error (ws transitioning):', err);
        return false;
      }
    }
    return false;
  }

  private noteWsAudioBackpressure(now: number, bufferedAmount: number): void {
    if (now - this.wsAudioBackpressureWindowStartedMs > this.wsAudioBackpressureWindowMs) {
      this.wsAudioBackpressureWindowStartedMs = now;
      this.wsAudioBackpressureCount = 0;
    }
    this.wsAudioBackpressureCount++;
    if (this.wsAudioBackpressureCount < this.wsAudioBackpressureReconnectThreshold) return;
    this.wsAudioBackpressureCount = 0;
    this.wsAudioBackpressureWindowStartedMs = now;
    console.warn('[FC] ws_audio_backpressure_reconnect', { bufferedAmount });
    this.setState('reconnecting');
    this.commandSubject.next({
      type: 'RECONNECTING',
      message: 'Reconectando por congestión de audio.',
      attempt: this.reconnectAttempts + 1,
      max: this.maxReconnectAttempts,
      reconnectable: true,
    });
    try {
      this.ws?.close(4001, 'audio_backpressure');
    } catch (error) {
      console.warn('[FC] ws_audio_backpressure_close_failed', error);
    }
  }

  /**
   * Fase 9.1 — Arms the outbound audio suppression window for `ms` milliseconds.
   * Always extends the current window (max of current and new) — never shortens.
   * Called from the component on AWAITING_GREETING (1500ms) to avoid echo / buffered
   * chunks triggering false barge-in at the start of greeting narration.
   */
  suppressOutboundAudioFor(ms: number, reason: SuppressReason): void {
    const until = Date.now() + ms;
    // Always extend the window (max of current and new) — never shorten
    if (until > this.audioOutputSuppressedUntilMs) {
      this.audioOutputSuppressedUntilMs = until;
      if ((window as any).__fcDebug?.enabled) {
        const key = `audioSuppressArmed_${reason}`;
        (window as any).__fcDebug[key] = ((window as any).__fcDebug[key] || 0) + 1;
      }
      console.log(`[FC] outbound_audio_suppressed reason=${reason} ms=${ms}`);
    }
  }

  /**
   * Anti-eco — Activa la supresión outbound SIN timer: bloquea todos los
   * envíos del mic al WebSocket hasta que llegue `mic_open`, playback_end o se
   * llame `releaseOutboundSuppression()`.
   * Llamar cuando arranca la narración de Eva
   * para evitar que el eco acústico llegue al server VAD de Gemini.
   */
  suppressOutboundUntilPlaybackReady(reason: SuppressReason): void {
    // Fix B (followup re-narration) — detectar transición false → true. Esto
    // marca el inicio de una NUEVA narración (no continuación del mismo turno).
    // Si la narración previa terminó y arranca una nueva (re-narration tras
    // state_resync, followup, etc.), resetear timestamps para medir el cap
    // desde este assistant turn.
    const isNewNarration = !this.outboundSuppressArmed_narration;
    this.outboundSuppressArmed_narration = true;
    this.releaseOnPlaybackEnd = false;
    // Extender la ventana de tiempo al máximo posible (year 2099) — efectivamente
    // infinita hasta que releaseOutboundSuppression() la limpie.
    this.audioOutputSuppressedUntilMs = 9999999999999;
    // Registrar timestamp de inicio para el gate anti-eco de barge-in.
    if (this.narrationStartTs === null) {
      this.narrationStartTs = performance.now();
    }
    // Watchdog: inicializar timestamp al momento de armar para que el contador
    // de 4s empiece desde el primer chunk, no desde el inicio del componente.
    //
    // SOLO en nueva narración. Un re-arm idempotente (el BE reenvía
    // AWAITING_RESPONSE mientras espera) NO debe reiniciar el reloj del
    // watchdog: hacerlo lo dejaba inalcanzable y el mic quedaba mudo.
    // Incidente 2026-08-05 (test 0ba4a82e): tras un avance in-block que Eva
    // nunca narró, el BE reenvió AWAITING_RESPONSE 59 veces en 59s; cada uno
    // pisaba este timestamp, el umbral de 15s nunca se cruzaba y la única
    // salida quedaba en el cap absoluto de 90s, que no alcanzó a vencer antes
    // de que el candidato abandonara. Durante una narración real los chunks
    // de Eva mantienen el valor fresco vía notifyEvaAudioActivity().
    if (isNewNarration) {
      this.lastEvaAudioChunkAtMs = Date.now();
    }
    // Bug A fix — registrar epoch-ms para el cap absoluto de 90s.
    // Fix B — solo resetear si es nueva narración (false → true). Si es
    // continuación del mismo turno (re-arm idempotente), preservar el ts
    // original para mantener el budget del watchdog de 90s coherente.
    if (isNewNarration) {
      this.suppressionArmedAtMs = Date.now();
      // Al iniciar nueva narración, el deadline anterior queda inválido.
      // Resetear para que armPlaybackEndDeadline regenere una nueva ventana.
      this.turnCompleteTsMs = null;
    }
    // Watchdog anti-deadlock: si Eva deja de producir audio por >4s Y no hay
    // audio local reproduciéndose, liberar el mic a la fuerza.
    // Bug A fix: el watchdog NO libera mientras localAudioIsPlaying (eso es
    // narración larga en cola, no deadlock). Solo libera cuando no hay audio
    // local Y no llegan chunks del BE por 4s.
    // Cap absoluto: 90s máximo de supresión en cualquier circunstancia.
    if (!this.suppressWatchdogInterval) {
      this.suppressWatchdogInterval = setInterval(() => {
        if (!this.outboundSuppressArmed_narration) return;
        // Cap absoluto de seguridad: 90s — nunca mantener la supresión más de esto.
        if (this.suppressionArmedAtMs > 0 && Date.now() - this.suppressionArmedAtMs > 90_000) {
          console.warn('[FC] outbound_suppression_absolute_cap_release — 90s exceeded');
          if ((window as any).__fcDebug?.enabled) {
            (window as any).__fcDebug.suppressWatchdogReleases =
              ((window as any).__fcDebug.suppressWatchdogReleases || 0) + 1;
          }
          this.releaseOutboundSuppression();
          return;
        }
        // Bug A fix — si el audio local sigue reproduciéndose, el watchdog
        // NO debe liberar: es narración larga, no deadlock.
        if (this.localAudioIsPlaying) return;
        // BE ya cerró el turno lógico (assistant_logical_complete) y nunca llegó
        // ningún chunk: la narración fue muda con certeza, no hace falta esperar
        // el budget normal de 15s/8s. Cubre greeting/ack/pregunta mudos (el caso
        // de followup_requested ya no re-arma la supresión, ver 'followup_requested').
        if (this.activeAssistantLogicalCompleteAtMs > 0 && !this.evaFirstChunkSentThisTurn) {
          const msSinceLogicalComplete = Date.now() - this.activeAssistantLogicalCompleteAtMs;
          if (msSinceLogicalComplete > this.WATCHDOG_SILENT_TURN_TIMEOUT_MS) {
            console.warn(`[FC] outbound_suppression_watchdog_release reason=silent_turn threshold_ms=${this.WATCHDOG_SILENT_TURN_TIMEOUT_MS} ms_since_logical_complete=${msSinceLogicalComplete}`);
            if ((window as any).__fcDebug?.enabled) {
              (window as any).__fcDebug.suppressWatchdogReleases =
                ((window as any).__fcDebug.suppressWatchdogReleases || 0) + 1;
            }
            this.releaseOutboundSuppression();
          }
          return;
        }
        // Fix D — split del threshold segun estado:
        //   - pending_first_chunk: Eva aun no envio chunks → TTFT alto es normal,
        //     budget 15s antes de declarar deadlock.
        //   - mid_stream_stuck: Eva ya habia enviado chunks y se congelo →
        //     deadlock real, budget 8s.
        const isPendingFirstChunk = !this.evaFirstChunkSentThisTurn;
        const threshold = isPendingFirstChunk
          ? this.WATCHDOG_FIRST_CHUNK_TIMEOUT_MS
          : this.WATCHDOG_MID_STREAM_TIMEOUT_MS;
        const reason = isPendingFirstChunk ? 'first_chunk_timeout' : 'mid_stream_stuck';
        const msSinceLastChunk = Date.now() - this.lastEvaAudioChunkAtMs;
        if (msSinceLastChunk > threshold) {
          // Causa D/E fix — para mid_stream_stuck (no pending_first_chunk), exigir
          // que los buffers del componente también estén vacíos antes de liberar.
          // Un drain momentáneo entre bursts de Gemini baja localAudioIsPlaying a
          // false (Causa E) y antes producía una liberación falsa. Con este check,
          // solo liberamos cuando NO hay audio agendado Y no llegan chunks por 8s.
          if (!isPendingFirstChunk && this.audioBuffersDrainedProvider !== null) {
            const buffersDrained = this.audioBuffersDrainedProvider();
            if (!buffersDrained) return; // draining between bursts — not a deadlock
          }
          console.warn(`[FC] outbound_suppression_watchdog_release reason=${reason} threshold_ms=${threshold} ms_since_last_chunk=${msSinceLastChunk} (no local playback)`);
          if ((window as any).__fcDebug?.enabled) {
            (window as any).__fcDebug.suppressWatchdogReleases =
              ((window as any).__fcDebug.suppressWatchdogReleases || 0) + 1;
          }
          this.releaseOutboundSuppression();
        }
      }, 1000);
    }
    if ((window as any).__fcDebug?.enabled) {
      (window as any).__fcDebug.outboundSuppressArmed_narration = true;
      const key = `audioSuppressArmed_${reason}`;
      (window as any).__fcDebug[key] = ((window as any).__fcDebug[key] || 0) + 1;
    }
    console.log(`[FC] outbound_suppress_until_playback_ready reason=${reason}`);
  }

  /**
   * Dead air detector — expone si el outbound está suprimido (fase de narración).
   * El componente lo usa como condición para NO acumular silencio bilateral
   * (si la supresión está armada, Eva está narrando; no es "aire muerto").
   */
  get isOutboundSuppressed(): boolean {
    return this.outboundSuppressArmed_narration || Date.now() < this.audioOutputSuppressedUntilMs;
  }

  /**
   * Defecto 2 (validación línea a línea) — "¿está sonando el audio de Eva
   * ahora?", no un efecto lateral de la supresión outbound. Con
   * `bargeInEnabled` (Stage 2), `notifyEvaFirstChunk()` NUNCA arma
   * `outboundSuppressArmed_narration` (por diseño: el mic debe seguir mandando
   * durante la narración para permitir barge-in) — `isOutboundSuppressed`
   * queda SIEMPRE false mientras Eva narra, justo el escenario con más eco.
   * `localAudioIsPlaying` (Bug A fix) sí refleja el estado real de
   * reproducción: lo setea el componente en notifyLocalAudioPlayingState(true)
   * cuando scheduledSources pasa de 0→1 (primer chunk agendado) y en
   * notifyLocalAudioPlayingState(false) cuando la cola vuelve a 0 — tanto por
   * el onended natural como por el poller ground-truth
   * (startPlaybackEndPoller) y por drainAndStopAudio (que espera a que el
   * audio agendado termine antes de cortar). Cubre narración + drenaje de
   * cola en ambos stages de barge-in.
   */
  get isEvaAudible(): boolean {
    return this.isOutboundSuppressed || this.localAudioIsPlaying;
  }

  /**
   * Tema paceScore-a-0 (bug real verificado en logs) — el gate del affect
   * engine preguntaba ACÚSTICA ("¿Eva suena?" vía isEvaAudible), no SEMÁNTICA
   * ("¿es el turno del candidato?"). `user_turn_complete` pone state='processing'
   * y a propósito NO cierra el mic ("keep sending audio for observation" —
   * ver el case de arriba): durante toda esa espera isEvaAudible es false
   * (Eva todavía no arrancó a hablar) pero el candidato YA NO tiene el turno.
   * El worklet seguía empujando RMS de silencio real hacia pushRms() y
   * paceScore se desangraba hasta 'very_slow' con el candidato simplemente
   * escuchando. `isCandidateTurn` exige, además de "Eva no suena", que el
   * estado conversacional sea realmente el del candidato hablando
   * (listening/follow_up, mismos estados que statusMessage muestra como
   * "Te escucha...") y que el mic esté físicamente abierto.
   */
  get isCandidateTurn(): boolean {
    return (this.stateSubject.value === 'listening'
         || this.stateSubject.value === 'follow_up')
      && this.micOpenSubject.value
      && !this.isEvaAudible;
  }

  /**
   * Dead air detector — envía un ping al BE indicando silencio bilateral.
   * Fire-and-forget: silencioso si el WebSocket está cerrado.
   */
  sendDeadAirPing(
    silenceMs: number,
    context: {
      question_idx?: number;
      question_id?: string;
      phase?: string;
      is_outbound_suppressed?: boolean;
      local_audio_playing?: boolean;
    } = {},
  ): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({
      type: 'dead_air_ping',
      protocol: FC_V5_PROTOCOL,
      silence_ms: silenceMs,
      question_index: context.question_idx ?? this.currentQuestionIndex,
      question_id: context.question_id ?? this.currentQuestionId,
      phase: context.phase ?? this.currentBackendPhase ?? (this.micOpenSubject.value ? 'candidate_listening' : this.currentNarrationPhase),
      mic_open: this.micOpenSubject.value,
      server_seq_seen: this.lastServerSeqApplied,
      is_outbound_suppressed: context.is_outbound_suppressed ?? this.isOutboundSuppressed,
      local_audio_playing: context.local_audio_playing ?? this.localAudioIsPlaying,
      ts: Date.now(),
    }));
    this.sendClientMetric('dead_air_ping_sent', 1, 'count');
  }

  /**
   * Anti-eco — Libera TODA supresión outbound activa, sin importar quién la
   * armó: la narration arm indefinida (`suppressOutboundUntilPlaybackReady`)
   * Y cualquier ventana CON TIMER abierta vía `suppressOutboundAudioFor`
   * (`audioOutputSuppressedUntilMs = 0`, ver abajo) — incluida la del piense
   * ('manual', ver `armThinkingOutboundSuppression` en el componente) y
   * cualquier otra que use ese mecanismo (greeting, echo-tail).
   *
   * R5 (ronda 5 de validación) — CORRECCIÓN de un comentario previo que
   * decía justo lo contrario ("no afecta ventanas con timer — solo la
   * narration arm"): eso nunca fue cierto, `audioOutputSuppressedUntilMs = 0`
   * dos líneas más abajo pisa CUALQUIER ventana temporizada abierta, sin
   * distinguir motivo. No es un bug de código — es el comportamiento
   * correcto A PROPÓSITO para los 5 callers actuales de este método
   * (ws.onclose, state_resync, el `mic_open` que manda el backend,
   * notifyPlaybackEnd y armPlaybackEndDeadline): todos representan un reset
   * TOTAL de la sesión de audio, donde dejar viva una ventana temporizada
   * ajena sería el bug real, no lo contrario. El único caller que necesita
   * un release ACOTADO (solo si ÉL armó la ventana) es
   * `releaseThinkingOutboundSuppression()` en el componente — y esa
   * acotación vive ahí, vía su propio flag local
   * (`thinkingOutboundSuppressionArmed`), NO acá: este método es
   * deliberadamente un martillo, no un bisturí. Cualquier caller nuevo que
   * necesite scope debe guardarse ANTES de llamarlo, igual que ese.
   */
  releaseOutboundSuppression(): void {
    // Capturar ANTES de limpiar — el componente necesita saber si esta
    // llamada realmente barrió algo (si no había nada armado, no hay piense
    // que re-armar del otro lado).
    const wasArmed =
      this.outboundSuppressArmed_narration ||
      Date.now() < this.audioOutputSuppressedUntilMs;
    this.outboundSuppressArmed_narration = false;
    this.releaseOnPlaybackEnd = false;
    this.suppressionArmedAtMs = 0;
    this.audioOutputSuppressedUntilMs = 0;
    this.narrationStartTs = null;
    // Resetear el ts que armó el deadline para que el próximo turno empiece limpio.
    this.turnCompleteTsMs = null;
    // Limpiar el watchdog: ya no hay supresión activa que vigilar.
    if (this.suppressWatchdogInterval) {
      clearInterval(this.suppressWatchdogInterval);
      this.suppressWatchdogInterval = null;
    }
    if (this.playbackEndDeadlineTimer) {
      clearTimeout(this.playbackEndDeadlineTimer);
      this.playbackEndDeadlineTimer = null;
    }
    if ((window as any).__fcDebug?.enabled) {
      (window as any).__fcDebug.outboundSuppressArmed_narration = false;
    }
    console.log('[FC] outbound_suppression_released');
    if (wasArmed) this.suppressionForceReleasedSubject.next();
  }

  // ---------------------------------------------------------------------------
  // FC Feature 2 — Resume buffer de falsa interrupción (patrón LiveKit)
  // ---------------------------------------------------------------------------

  /**
   * FC Feature 2 — Llamado por el componente al recibir 'interrupted' (antes de
   * stopAllAudio). Guarda los chunks de la cola no reproducidos y abre la ventana
   * de evaluación de 4s. Si la ventana expira sin habla real ni chunks nuevos de
   * Eva → emitir resumeAudio$ para re-encolar (falsa interrupción).
   * Si el budget es 0 o no hay chunks para guardar, no hace nada.
   */
  captureResumeBuffer(pendingChunks: ArrayBuffer[]): void {
    if (this.falseInterruptBudget <= 0) return;
    if (pendingChunks.length === 0) return;

    // Limpiar ventana anterior si existía
    this.clearFalseInterruptTimer();
    this.resumeBuffer = pendingChunks.map((data, i) => ({ data, seq: i }));
    this.resumeBufferTimestamp = Date.now();
    this.falseInterruptSawUserTranscript = false;
    this.falseInterruptSawNewEvaChunk = false;

    console.log(`[FC] false_interrupt_window_open chunks=${this.resumeBuffer.length}`);

    this.falseInterruptTimer = setTimeout(() => {
      this.falseInterruptTimer = null;
      // Si en la ventana llegó habla real o chunks nuevos de Eva → ya fue descartado.
      if (this.resumeBuffer.length === 0) return;
      // Verificar RMS silencioso (el componente rastrea esto; aquí solo confiamos
      // en la ausencia de userSpeechActiveForMetric y falseInterruptSawUserTranscript).
      if (this.falseInterruptSawUserTranscript || this.falseInterruptSawNewEvaChunk) {
        this.clearResumeBuffer();
        return;
      }
      // Falsa interrupción confirmada — re-encolar y reproducir.
      const chunks = this.resumeBuffer.map(e => e.data);
      this.resumeBuffer = [];
      this.falseInterruptBudget--;
      console.log(`[FC] false_interruption_resume chunks=${chunks.length} budget_left=${this.falseInterruptBudget}`);
      if ((window as any).__fcDebug?.enabled) {
        (window as any).__fcDebug.falseInterruptionResumes =
          ((window as any).__fcDebug.falseInterruptionResumes || 0) + 1;
      }
      // Re-armar supresión outbound (la reanudación es playback de Eva — anti-eco).
      this.suppressOutboundUntilPlaybackReady('narration');
      // Emitir chunks al componente para re-encolar.
      this.resumeAudioSubject.next(chunks);
    }, FcSessionService.FC_FALSE_INTERRUPT_RESUME_MS);
  }

  /** Limpia el buffer y cancela el timer de falsa interrupción. */
  clearResumeBuffer(): void {
    this.resumeBuffer = [];
    this.clearFalseInterruptTimer();
  }

  private clearFalseInterruptTimer(): void {
    if (this.falseInterruptTimer !== null) {
      clearTimeout(this.falseInterruptTimer);
      this.falseInterruptTimer = null;
    }
  }

  /**
   * Bug A fix — Llamado por el componente cuando el estado de reproducción local
   * cambia. El componente setea isPlaying=true cuando scheduleChunk agrega el primer
   * source y isPlaying=false cuando el último source.onended dispara (cola vacía).
   * El service usa esto para decidir si el watchdog puede liberar la supresión y
   * para implementar el defer-to-playback-end de assistant_logical_complete.
   */
  notifyLocalAudioPlayingState(isPlaying: boolean): void {
    this.localAudioIsPlaying = isPlaying;
    if (!isPlaying && this.releaseOnPlaybackEnd) {
      this.notifyPlaybackEnd();
    }
  }

  /**
   * Contrato de wire NUEVO (timers piense/pregunta) — avisa al backend cuando
   * se agota cada ventana, para que el agente actúe en sincronía con lo que
   * el candidato ve en pantalla. Fire-and-forget, como el resto de los
   * client_* de esta clase.
   */
  sendClientQuestionTimer(
    phase: 'thinking_ended' | 'answer_budget_expired',
    questionIdx: number | undefined,
    reason?: 'timeout' | 'voice_command',
  ): void {
    if (this.qualityMode === 'low') {
      this.lowRuntime?.onClientQuestionTimer(phase, questionIdx, reason);
      return;
    }
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const payload: Record<string, unknown> = {
      type: 'client_question_timer',
      protocol: FC_V5_PROTOCOL,
      question_idx: questionIdx ?? this.currentQuestionIndex,
      phase,
      ts: Date.now(),
    };
    // reason solo aplica (y es opcional) en thinking_ended, per contrato.
    if (reason !== undefined) payload['reason'] = reason;
    this.ws.send(JSON.stringify(payload));
    console.log('[FC-SERVICE] client_question_timer sent:', phase, reason ?? '');
  }

  /**
   * Contrato de wire NUEVO (VC detection — CONTRATO-VC-DETECTION.md §2/§4.3 /
   * ADENDA) — reporta al agente Python una transición de nivel del
   * `VcScorerService` para que cuente strikes y decida enforcement
   * SERVER-SIDE (así un reload no resetea el contador — ver ADENDA "Ya
   * existe VcScorerService"). El FE solo detecta/reporta; Eva narra los
   * avisos. Fire-and-forget como el resto de los `client_*` de esta clase,
   * pero OJO — a diferencia de `sendClientQuestionTimer`/`sendClientSpeechState`:
   *   1) SÍ lleva `protocol` (corrección post-merge del BE: protocol/inbound.py
   *      InboundClientProctoringEvent es Pydantic `extra="forbid"` con
   *      `protocol` requerido);
   *   2) NO lleva `ts` ni ningún otro campo — son estos 6 keys exactos o
   *      Pydantic rechaza el mensaje ENTERO y en silencio. El tipo
   *      `FcV5ClientProctoringEvent` (sin index signature) hace que un campo
   *      de más acá mismo ya tire error de compilación, no solo en runtime.
   */
  sendProctoringEvent(
    kind: FcV5ProctoringKind,
    score: number,
    families: string[],
    signals: string[],
  ): void {
    // ponytail: quality_mode=low es fc-low (HTTP + TTS de navegador, ver
    // connectLow) — no hay WS ni agente Python del lado del server con quien
    // hablar este protocolo. La detección de VC en low queda solo como
    // evidencia FE (AntiFraudSnapshot al terminar, ver
    // sendAntiFraudSnapshot/beaconFcAntiFraudSnapshot en fc-session.component.ts);
    // sin enforcement hablado ni suspensión en vivo. Si low llega a usarse en
    // producción real, esto necesita su propia escalera (ver TRAMPAS
    // CONOCIDAS del contrato).
    if (this.qualityMode === 'low') return;
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const payload: FcV5ClientProctoringEvent = {
      type: 'client_proctoring_event',
      protocol: FC_V5_PROTOCOL,
      kind,
      score,
      families,
      signals,
    };
    this.ws.send(JSON.stringify(payload));
    console.log('[FC-SERVICE] client_proctoring_event sent:', kind, score, families, signals);
  }

  /**
   * Contrato de wire NUEVO (fc-affect-adaptive-tuning) — le avisa al backend
   * si el ASR local ve al candidato a mitad de una elocución (interim en
   * curso o frase colgada, ver speech-completeness.util.ts) o si cerró la
   * idea/se calló. El caller (fc-session.component.ts) es responsable de
   * mandar esto SOLO en transiciones (false→true, true→false) y throttlear —
   * este método no dedupe ni throttlea, fire-and-forget como el resto de los
   * client_* de esta clase.
   */
  sendClientSpeechState(midUtterance: boolean, questionIdx: number | undefined): void {
    // quality_mode=low — no WS; drive EOS grace cancel/arm on the fc-low runtime.
    if (this.qualityMode === 'low') {
      if (midUtterance) {
        this.lowRuntime?.onSpeechActive();
      } else {
        this.lowRuntime?.onSpeechSettled();
      }
      return;
    }
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({
      type: 'client_speech_state',
      protocol: FC_V5_PROTOCOL,
      question_idx: questionIdx ?? this.currentQuestionIndex,
      mid_utterance: midUtterance,
      ts: Date.now(),
    }));
    console.log('[FC-SERVICE] client_speech_state sent:', midUtterance);
  }

  /** LOW — Continuar del modal de pausa: Eva retoma la entrevista. */
  resumeCandidatePause(): void {
    if (this.qualityMode !== 'low') return;
    this.lowRuntime?.resumePause();
  }

  /** Comando por botón (pasar/repetir): equivale a que el candidato lo diga por
   *  voz. El agente lo convierte en un turno sintético y lo procesa igual. */
  sendClientCommand(command: 'pass' | 'repeat' | 'clarify' | 'context' | 'pause', opts?: { timeUp?: boolean }): void {
    if (this.qualityMode === 'low') {
      if (opts) this.lowRuntime?.onClientCommand(command, opts);
      else this.lowRuntime?.onClientCommand(command);
      return;
    }
    // High path: NO TOCAR — ese backend solo conoce pass/repeat como
    // client_command (protocol/inbound.py sigue con Literal["pass","repeat"]).
    // clarify se ignora acá igual: Live ya escucha las palabras en el PCM.
    if (command === 'clarify' || command === 'context' || command === 'pause') {
      console.log(`[FC-SERVICE] client_command ${command} ignored on high (unsupported by BE)`);
      return;
    }
    if (this.ws?.readyState !== WebSocket.OPEN) {
      console.warn('[FC-SERVICE] client_command skipped: WS not open');
      return;
    }
    this.ws.send(JSON.stringify({
      type: 'client_command',
      protocol: FC_V5_PROTOCOL,
      command,
    }));
    console.log('[FC-SERVICE] client_command sent:', command);
    // 'pass' hace avanzar: el front queda esperando la próxima pregunta. Armar
    // el watchdog ya acá cubre el caso en que el server cuelga ANTES de emitir
    // next_question (nunca entraría a 'transitioning'). Se re-arma en
    // next_question y se limpia en assistant_turn_started/state_resync.
    if (command === 'pass') this.armTransitionWatchdog();
  }

  private sendPlaybackAck(reason: string): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const assistantTurnId = this.activeAssistantTurnId;
    if (!assistantTurnId) {
      console.warn('[FC-SERVICE] playback_ack skipped: assistant_turn_id missing');
      return;
    }
    if (this.playbackAckSentForTurnId === assistantTurnId) {
      return;
    }
    const ackAt = new Date().toISOString();
    const playedDurationMs = this.activeAssistantTurnStartedAtMs > 0
      ? Math.max(0, Date.now() - this.activeAssistantTurnStartedAtMs)
      : 0;
    this.ws.send(JSON.stringify({
      type: 'playback_ack',
      protocol: FC_V5_PROTOCOL,
      assistant_turn_id: assistantTurnId,
      question_id: this.currentQuestionId,
      question_index: this.currentQuestionIndex,
      last_eva_audio_seq: this.lastEvaAudioSeq,
      reason: reason === 'playback_end' ? 'playback_ended' : reason,
      local_audio_playing: this.localAudioIsPlaying,
      played_duration_ms: playedDurationMs,
      ack_at: ackAt,
    }));
    this.pendingPlaybackAck = false;
    this.playbackAckSentForTurnId = assistantTurnId;
  }

  /**
   * Bug A fix — Llamado por el componente cuando el último source de Eva terminó
   * de reproducirse (scheduledSources.length llega a 0 tras onended).
   * Si la supresión fue diferida a playback-end (releaseOnPlaybackEnd=true),
   * espera un tail de ~300ms (anti-eco residual) y luego libera el mic.
   * Solo actúa para phase=narration (greeting y farewell ya tienen su propio path).
   */

  /**
   * Returns and clears pending timer budgets stashed from the latest mic_open
   * that carried thinking_time_s / answer_budget_s. Used by the component when
   * pendingQuestionTimerArm is null (greeting / followup without next_question).
   */
  consumePendingMicOpenTimerArm(): {
    thinkingTimeS: number;
    answerBudgetS: number;
    questionIdx: number | undefined;
    freshWindow?: boolean;
    confirmAdvance?: boolean;
    pauseS?: number;
  } | null {
    const arm = this.pendingMicOpenTimerArm;
    this.pendingMicOpenTimerArm = null;
    return arm;
  }

  /**
   * quality_mode=low — feed local ASR transcript into the fc-low runtime.
   * No-op on the high (WS) path.
   */
  noteLocalTranscript(
    text: string,
    opts?: { isFinal?: boolean; incomplete?: boolean },
  ): void {
    if (this.qualityMode !== 'low') return;
    this.lowRuntime?.noteTranscript(text, opts);
  }

  /**
   * quality_mode=low — ¿este texto del ASR local es el propio TTS de Eva (eco)?
   * Fuente única: FcLowRuntime.isLikelyEcho. false en high / sin runtime.
   */
  isLikelyLowTtsEcho(text: string): boolean {
    if (this.qualityMode !== 'low') return false;
    return this.lowRuntime?.isLikelyEcho(text) ?? false;
  }

  /**
   * quality_mode=low — el recognizer del browser reinicia (true) / ya arrancó (false):
   * el runtime suspende el EOS durante el hueco. No-op en high / sin runtime.
   */
  setLowRecognizerRestarting(restarting: boolean): void {
    if (this.qualityMode !== 'low') return;
    this.lowRuntime?.setRecognizerRestarting(restarting);
  }

  /**
   * quality_mode=low — umbral de cierre por silencio ADAPTATIVO vigente (ms) para
   * `text`; null en high / sin runtime. El componente deriva de acá la gracia de
   * mid-utterance para que nunca supere al umbral del runtime.
   */
  getLowEosThresholdMs(text?: string): number | null {
    if (this.qualityMode !== 'low') return null;
    return this.lowRuntime?.getEosThresholdMs(text) ?? null;
  }

    notifyPlaybackEnd(): void {
    if (this.pendingPlaybackAck) {
      this.sendPlaybackAck('playback_end');
    }
    if (!this.releaseOnPlaybackEnd) return;
    // assistant_logical_complete ya llegó pero diferimos la liberación al fin del playback.
    // Esperar un tail corto (300ms) para limpiar el eco residual de reverberación.
    setTimeout(() => {
      if (!this.releaseOnPlaybackEnd) return; // ya fue liberado por otra vía
      console.log('[FC] outbound_suppression_released reason=playback_end');
      this.releaseOutboundSuppression();
    }, 300);
  }

  /**
   * Bug log25.5 — provider opcional que el component setea via
   * setPlaybackTailMsProvider(). Permite que el service prediga el tail real
   * del playback (nextPlayTime - playbackCtx.currentTime) sin acoplar al
   * component. Si no está seteado, fallback al deadline base (wall-clock).
   */
  private playbackTailMsProvider: (() => number) | null = null;

  /**
   * Bug log25.5 — el component setea este provider en ngOnInit para que el
   * service pueda predecir cuánto audio queda agendado en el playbackCtx.
   * Sin esto, el deadline base de 5000ms wall-clock corta audio que todavía
   * está sonando con jitter alto.
   */
  public setPlaybackTailMsProvider(provider: (() => number) | null): void {
    this.playbackTailMsProvider = provider;
  }

  /**
   * Causa D/E fix — provider que el componente registra para exponer si los
   * buffers de audio (scheduledSources + jitterBuffer) están completamente
   * drenados Y el AudioContext ya pasó el último punto agendado.
   * El watchdog mid_stream_stuck lo usa para distinguir drain momentáneo
   * (burst-gap normal) de deadlock real (buffers vacíos + sin nuevos chunks).
   */
  private audioBuffersDrainedProvider: (() => boolean) | null = null;

  public setAudioBuffersDrainedProvider(provider: (() => boolean) | null): void {
    this.audioBuffersDrainedProvider = provider;
  }

  /**
   * Bug log25.5 — calcula el deadline efectivo combinando el base wall-clock
   * con el tail real del playback (si el component lo provee), respetando
   * el cap absoluto playbackEndDeadlineMaxAgeMs medido desde turnCompleteTsMs.
   *
   * @param playbackTailMs tail explícito (override del provider). 0 = sin tail.
   */
  private computeEffectiveDeadlineMs(playbackTailMs: number): number {
    // Buffer anti-jitter de 1s sobre el tail real para tolerar reagendado.
    const tailWithBuffer = playbackTailMs > 0 ? playbackTailMs + 1000 : 0;
    const effective = Math.max(this.playbackEndDeadlineMs, tailWithBuffer);

    // Cap superior: no permitir deadline > maxAge restante desde assistant_logical_complete.
    if (this.turnCompleteTsMs !== null) {
      const ageSinceTurnComplete = Date.now() - this.turnCompleteTsMs;
      const maxAllowedMs = this.playbackEndDeadlineMaxAgeMs - ageSinceTurnComplete;
      if (maxAllowedMs <= 0) return this.playbackEndDeadlineMs; // ya pasamos el cap, no extender
      return Math.min(effective, maxAllowedMs);
    }
    return effective;
  }

  private armPlaybackEndDeadline(playbackTailMs: number = 0): void {
    if (this.playbackEndDeadlineTimer) {
      clearTimeout(this.playbackEndDeadlineTimer);
    }
    // Fix C v2 — registrar el momento en que se armó por primera vez tras
    // assistant_logical_complete. El cap absoluto se mide desde acá (NO desde
    // suppressionArmedAtMs, que mezclaba duración total de narración con
    // tiempo post-assistant_logical_complete y cortaba followups largos).
    if (this.turnCompleteTsMs === null) {
      this.turnCompleteTsMs = Date.now();
    }
    // Bug log25.5 — si el caller no pasó tail explícito, intentar obtenerlo
    // del provider que el component setea en ngOnInit.
    const tailMs = playbackTailMs > 0
      ? playbackTailMs
      : (this.playbackTailMsProvider ? this.playbackTailMsProvider() : 0);
    const deadlineMs = this.computeEffectiveDeadlineMs(tailMs);
    this.playbackEndDeadlineTimer = setTimeout(() => {
      this.playbackEndDeadlineTimer = null;
      if (!this.releaseOnPlaybackEnd) return;
      console.warn('[FC] playback_end_deadline_release — forcing release after assistant_logical_complete');
      // [PLAYBACK-END] Si el poller hubiera funcionado, no deberíamos llegar acá.
      // Métrica: deadlineReleases (separada de suppressWatchdogReleases).
      if ((window as any).__fcDebug) {
        (window as any).__fcDebug.deadlineReleases =
          ((window as any).__fcDebug.deadlineReleases || 0) + 1;
      }
      this.sendClientMetric('playback_stalled', 1, 'count');
      this.notifyPlaybackStalled();
      this.localAudioIsPlaying = false;
      this.releaseOutboundSuppression();
    }, deadlineMs);
  }

  /**
   * Fix C — Re-arma el deadline cuando llega un chunk de audio DESPUÉS de
   * assistant_logical_complete (releaseOnPlaybackEnd=true). El deadline original corre en
   * wall-clock (2.5s→5s) mientras chunks rebufereados empujan
   * lastScheduledEndTime; sin re-armar, el deadline dispara antes que el
   * playback real termine.
   *
   * Cap absoluto: si pasaron más de playbackEndDeadlineMaxAgeMs (30s) desde
   * que se armó el deadline post assistant_logical_complete (turnCompleteTsMs), NO
   * re-armar — el cinturón final dispara.
   *
   * Fix C v2 — el cap se mide desde turnCompleteTsMs, NO desde
   * suppressionArmedAtMs. Antes se mezclaba duración total de narración con
   * tiempo post-assistant_logical_complete, lo que cortaba el final de followups largos.
   *
   * Bug log25.5 — acepta playbackTailMs para predecir el tail real del
   * playback (nextPlayTime - playbackCtx.currentTime). El deadline efectivo
   * es Math.max(playbackEndDeadlineMs, playbackTailMs + 1000ms buffer).
   *
   * No-op si releaseOnPlaybackEnd=false (assistant_logical_complete aún no llegó).
   */
  bumpPlaybackEndDeadline(playbackTailMs: number = 0): void {
    if (!this.releaseOnPlaybackEnd) return;
    if (this.turnCompleteTsMs !== null &&
        Date.now() - this.turnCompleteTsMs >= this.playbackEndDeadlineMaxAgeMs) {
      return;
    }
    this.armPlaybackEndDeadline(playbackTailMs);
  }

  /**
   * Fase 1 — Registra actividad de voz del usuario tras cada chunk enviado.
   * Maneja los side-effects que antes estaban acoplados a sendAudio:
   * isSpeaking, lastSpeechTime, resetSilenceDetection.
   * Debe llamarse desde el componente DESPUÉS de sendAudio, por cada chunk PCM.
   */
  markUserSpeechActivity(): void {
    this.isSpeaking = true;
    this.lastSpeechTime = Date.now();
    this.resetSilenceDetection();
  }

  /**
   * Fase 1 — Reenvía al backend el heartbeat de captura del worklet.
   * Solo envía si el WebSocket está abierto; silently no-op otherwise.
   */
  sendCaptureHeartbeat(payload: { seq: number; chunks_in_window: number; ts: number }): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.lastCaptureSeq = payload.seq;
    this.ws.send(JSON.stringify({
      type: 'capture_heartbeat',
      protocol: FC_V5_PROTOCOL,
      capture_seq: payload.seq,
      chunks_in_window: payload.chunks_in_window,
      mic_open: this.micOpenSubject.value,
      ts: payload.ts,
    }));
  }

  /**
   * [MIC-GATE DEBUG] Estado vivo de la compuerta anti-eco para el snapshot
   * de diagnóstico (plan mic-deadlock: localAudioIsPlaying,
   * releaseOnPlaybackEnd, suppressionAgeMs visibles en consola).
   */
  getMicGateDebug(): { localAudioIsPlaying: boolean; releaseOnPlaybackEnd: boolean; suppressionAgeMs: number } {
    return {
      localAudioIsPlaying: this.localAudioIsPlaying,
      releaseOnPlaybackEnd: this.releaseOnPlaybackEnd,
      suppressionAgeMs:
        this.suppressionArmedAtMs > 0 ? Date.now() - this.suppressionArmedAtMs : 0,
    };
  }

  /**
   * Reporta al BE que el playback local se quedó estancado.
   *
   * Reporte fc-v5 de stall de playback.
   * `stall_ms` se infiere desde `turnCompleteTsMs` si el caller no lo pasa,
   * o fallback al `playbackEndDeadlineMs` actual.
   *
   * @param stallMsOverride opcional — duración medida del stall (ms)
   */
  notifyPlaybackStalled(stallMsOverride?: number): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const stallMs = stallMsOverride !== undefined && stallMsOverride >= 0
      ? Math.max(0, Math.floor(stallMsOverride))
      : (this.turnCompleteTsMs !== null
          ? Math.max(0, Date.now() - this.turnCompleteTsMs)
          : this.playbackEndDeadlineMs);
    this.ws.send(JSON.stringify({
      type: 'playback_stalled',
      protocol: FC_V5_PROTOCOL,
      assistant_turn_id: this.activeAssistantTurnId,
      last_eva_audio_seq: this.lastEvaAudioSeq,
      buffered_chunks: 0,
      reason: 'source_onended_missing',
      stall_ms: stallMs,
    }));
  }
  
  /**
   * Ola 3 — Envía una métrica de cliente. Con el WebSocket abierto va al backend
   * (`client_metric`); sin WS (quality_mode=low, o high con el WS caído) va a RUM
   * como `fc_client_metric`. `extra` solo viaja a RUM: el BE high usa extra=forbid.
   */
  sendClientMetric(
    name: string,
    value: number,
    unit?: 'ms' | 'count',
    extra?: Record<string, string | number | boolean>,
  ): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const payload: Record<string, unknown> = {
        type: 'client_metric',
        protocol: FC_V5_PROTOCOL,
        name,
        value,
        assistant_turn_id: this.activeAssistantTurnId,
        ts: Date.now(),
      };
      if (unit !== undefined) payload['unit'] = unit;
      this.ws.send(JSON.stringify(payload));
      return;
    }
    this.rumMetrics.report(name, value, unit, {
      quality_mode: this.metricsQualityMode ?? 'unknown',
      ...(typeof this.currentQuestionIndex === 'number' ? { question_index: this.currentQuestionIndex } : {}),
      ...extra,
    });
  }

  /**
   * Affect adaptation hint (fc-v5) — FE affect engine → session knobs.
   * Fire-and-forget; agent clamps deltas + TTL. Never used for scoring.
   */
  sendCandidateAdaptationHint(payload: Record<string, unknown>): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(
        JSON.stringify({
          protocol: FC_V5_PROTOCOL,
          ...payload,
          type: 'candidate_adaptation_hint',
        }),
      );
    } catch (e) {
      console.warn('[FC-SERVICE] adaptation_hint send failed', e);
    }
  }

  // Bug FE-3 — rollup periódico de métricas críticas del incidente cdf3bdad
  // al BE vía client_metric. Llamado por el componente cada 15s.
  private lastTelemetryRollup: Record<string, number> = {};

  flushClientTelemetryRollup(): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const dbg = (window as any).__fcDebug;
    if (!dbg?.enabled) return;

    const counters: Array<[string, string]> = [
      ['audio_suppress_armed_narration', 'audioSuppressArmed_narration'],
      ['audio_suppress_armed_greeting', 'audioSuppressArmed_greeting'],
      ['audio_chunks_suppressed_outbound', 'audioChunksSuppressedOutbound'],
      ['ws_send_calls', 'wsSendCalls'],
      ['eva_chunks_received', 'evaChunksReceived'],
      ['interrupted_rejected_no_local_speech', 'interruptedRejectedNoLocalSpeech'],
      ['bargein_ignored_too_early', 'bargeinIgnoredTooEarly'],
      ['suppression_refreshed_on_first_chunk', 'suppressionRefreshedOnFirstChunk'],
    ];

    for (const [metricName, dbgKey] of counters) {
      const current = (dbg[dbgKey] as number) || 0;
      const prev = this.lastTelemetryRollup[metricName] || 0;
      const delta = current - prev;
      if (delta <= 0) continue;
      this.sendClientMetric(metricName, delta, 'count');
      this.lastTelemetryRollup[metricName] = current;
    }
  }

  /**
   * Ola 3 — Llamado desde el worklet handler del componente cuando el RMS
   * del mic supera el umbral de actividad tras un período con voz. El service
   * trackea el estado y emite `fe_user_speech_end` al caer.
   */
  onUserMicRmsActivity(rms: number, threshold: number): void {
    const isActive = rms > threshold;
    if (isActive) {
      // Rising edge: usuario está hablando.
      if (!this.userSpeechActiveForMetric) {
        this.userSpeechActiveForMetric = true;
      }
      // Cancelar el debounce de eva_thinking si el usuario retoma voz
      // (aún no llegó el turno de Eva).
      if (this.evaThinkingDebounceTimer !== null) {
        clearTimeout(this.evaThinkingDebounceTimer);
        this.evaThinkingDebounceTimer = null;
      }
    } else if (!isActive && this.userSpeechActiveForMetric) {
      // Falling edge: usuario dejó de hablar.
      this.userSpeechActiveForMetric = false;
      const nowMs = Date.now();
      // Ola 2.4 — registrar epoch-ms del fin de voz para first_audio_play_latency_ms.
      this.lastUserSpeechEndTs = nowMs;
      this.sendClientMetric('fe_user_speech_end', nowMs, 'ms');
      // NO transicionar a eva_thinking por una simple caída de RMS: el candidato
      // puede estar haciendo una pausa a mitad de respuesta (respira/piensa y
      // retoma). Antes el debounce de 800ms flipaba a "Eva está pensando" con el
      // candidato todavía en su turno. El indicador "Eva está pensando" ahora lo
      // maneja el estado 'processing' (user_turn_complete del backend = el turno
      // realmente terminó). Mientras el mic siga abierto se muestra "Te escucha".
    }
  }

  /**
   * Ola 3 Stage 2 — Notifica al service que hubo voz local en el chunk actual.
   * El componente llama esto cada vez que el worklet produce un chunk con
   * RMS > threshold MIENTRAS el audio de Eva está sonando.
   * Mantiene una ventana rodante de 1.5s para el min-speech gate de interrupted.
   */
  /** Sustained local speech (ms) before soft barge-in on browser TTS. */
  private static readonly LOW_SOFT_BARGE_IN_MS = 450;

  notifyLocalSpeechWindow(rms: number, threshold: number): void {
    if (rms <= threshold) return;
    const now = Date.now();
    if (this.localSpeechWindowStartMs === 0) {
      this.localSpeechWindowStartMs = now;
    }
    // Ventana de 1.5s: cuánto tiempo lleva el usuario hablando continuamente.
    this.lastLocalSpeechWindowMs = now - this.localSpeechWindowStartMs;

    // Soft barge-in por RMS DESACTIVADO en LOW: sin AEC/VAD de servidor, el mic
    // capta la PROPIA voz de Eva (browser TTS por los parlantes) y dispara un
    // barge-in falso — cortaba la narración a mitad y arrancaba el mic/countdown
    // encima de Eva (y en el saludo abortaba el two-beat, dejando la Q1 sin
    // narrar). El candidato interrumpe de forma explícita con el comando de voz
    // "Eva quiero contestar" (answer_now), que sí es inequívoco. Se mantiene la
    // ventana rodante de arriba solo para métricas/telemetría.
  }

  /** Resetea la ventana de voz local (llamar cuando el audio de Eva termina o se corta). */
  resetLocalSpeechWindow(): void {
    this.lastLocalSpeechWindowMs = 0;
    this.localSpeechWindowStartMs = 0;
  }

  /**
   * Ola 3 — Marca la llegada del primer chunk de audio de Eva en el turno actual.
   * Llamado por el componente en `enqueueAudio` al recibir el primer chunk.
   * También activa la supresión indefinida de outbound para evitar eco acústico.
   */
  notifyEvaFirstChunk(): void {
    if (this.evaFirstChunkSentThisTurn) return;
    this.evaFirstChunkSentThisTurn = true;
    const nowMs = Date.now();
    this.sendClientMetric('fe_first_eva_chunk', nowMs, 'ms');
    // D2 — fijar la ancla del turno en el primer chunk (ancla más robusta:
    // disponible en greeting, narraciones y respuestas a preguntas).
    if (this.turnStartAnchorTs === null) {
      this.turnStartAnchorTs = nowMs;
    }
    // Ola 2.3 — Eva respondió: limpiar el estado eva_thinking y cancelar debounce.
    if (this.evaThinkingDebounceTimer !== null) {
      clearTimeout(this.evaThinkingDebounceTimer);
      this.evaThinkingDebounceTimer = null;
    }
    // FC Feature 1 — cancelar cap timer al recibir primer chunk de Eva.
    if (this.evaThinkingCapTimer !== null) {
      clearTimeout(this.evaThinkingCapTimer);
      this.evaThinkingCapTimer = null;
    }
    if (this.stateSubject.value === 'eva_thinking') {
      this.setState('narrating');
    }
    // FC Feature 2 — llegó chunk de Eva: si hay ventana de falsa interrupción activa,
    // marcar como interrupción REAL (el modelo respondió a la interrupción → descartamos buffer).
    if (this.falseInterruptTimer !== null) {
      this.falseInterruptSawNewEvaChunk = true;
      this.clearResumeBuffer();
    }
    if (!this.bargeInEnabled) {
      // Stage 1: anti-eco backstop — asegurar supresión activa desde el primer chunk.
      if (!this.outboundSuppressArmed_narration) {
          this.suppressOutboundUntilPlaybackReady('narration');
      } else {
        // Bug FE-1 (incidente cdf3bdad) — supresión sigue armada del turno
        // anterior (releaseOnPlaybackEnd). Refrescar timestamps para que el
        // watchdog NO dispare prematuro sobre el turno nuevo y narrationStartTs
        // no quede nulled por resetEvaMetricsTurn.
        this.lastEvaAudioChunkAtMs = Date.now();
        if (this.narrationStartTs === null) {
          this.narrationStartTs = performance.now();
        }
        if ((window as any).__fcDebug?.enabled) {
          (window as any).__fcDebug.suppressionRefreshedOnFirstChunk =
            ((window as any).__fcDebug.suppressionRefreshedOnFirstChunk || 0) + 1;
        }
      }
    } else {
      // Stage 2: no armamos supresión. PERO seteamos narrationStartTs
      // incondicionalmente para que el gate anti-eco de 4s (interrupted ~588-598)
      // funcione aunque la supresión no esté activa.
      if (this.narrationStartTs === null) {
        this.narrationStartTs = performance.now();
      }
    }
  }

  /**
   * Ola 3 — Marca el inicio de reproducción real del primer chunk del turno
   * (post jitter buffer — cuando `flushJitterBuffer` arranca el playback).
   *
   * [FC_METRIC] Bug fix Ola 2.4: el flag `evaFirstPlaySentThisTurn` solo se
   * consume cuando ya existe `lastUserSpeechEndTs` (respuesta de Eva al usuario).
   * Si aún no hay voz previa (narración de pregunta / greeting), enviamos
   * `fe_first_eva_play` pero NO levantamos el flag — así la siguiente llamada
   * (la respuesta real) puede medir la latencia correctamente.
   */
  notifyEvaFirstPlay(): void {
    if (this.evaFirstPlaySentThisTurn) return;
    const nowMs = Date.now();
    this.sendClientMetric('fe_first_eva_play', nowMs, 'ms');
    // [FC_METRIC] Solo marcar como "ya procesado" y medir latencia cuando el
    // usuario realmente habló antes. Sin speech_end_ts → es narración de pregunta
    // o greeting; dejamos el flag libre para la respuesta real del turno.
    if (this.lastUserSpeechEndTs !== null) {
      const latencyMs = nowMs - this.lastUserSpeechEndTs;
      // D2c fix — consumir y limpiar lastUserSpeechEndTs al medir, para que no
      // quede stale en turnos de narración que siguen sin speech previo.
      this.lastUserSpeechEndTs = null;
      // Freshness cap: un fin-de-habla de hace >15s es huérfano de un turno
      // anterior que no generó respuesta directa — medirlo daría una latencia
      // falsa gigante que envenena los dashboards. Descartar sin consumir flag.
      if (latencyMs > 15_000) {
        console.warn('[FC_METRIC] firstAudioPlayLatencyMs descartada por stale:', latencyMs);
        return;
      }
      this.evaFirstPlaySentThisTurn = true;
      this.sendClientMetric('first_audio_play_latency_ms', latencyMs, 'ms');
      // Historial capped en 20 para inspección en QA.
      this.firstAudioPlayLatencyHistory.push(latencyMs);
      if (this.firstAudioPlayLatencyHistory.length > 20) {
        this.firstAudioPlayLatencyHistory.shift();
      }
      if ((window as any).__fcDebug?.enabled) {
        (window as any).__fcDebug.firstAudioPlayLatencyMs = latencyMs;
      }
      // Log por turno (se emite UNA vez por turno gracias al flag).
      console.log('[FC_METRIC] firstAudioPlayLatencyMs=', latencyMs);
    }
  }

  /**
   * D2 — Métrica de primer audio incondicional.
   * Se registra SIEMPRE (greeting, narraciones y respuestas): mide el tiempo
   * desde el primer chunk del turno (`turnStartAnchorTs`, fijado en
   * `notifyEvaFirstChunk`) hasta el primer `source.start()` real del turno
   * (llamado desde `flushJitterBuffer` → `notifyEvaFirstPlay`).
   *
   * Ancla elegida: llegada del primer chunk de audio de Eva en el turno
   * (`notifyEvaFirstChunk`). Es el evento más robusto porque está disponible
   * en TODOS los turnos sin depender del estado del micrófono del usuario.
   *
   * Emite `first_audio_play_unconditional_ms` via sendClientMetric,
   * setea `__fcDebug.firstAudioPlayUnconditionalMs` y acumula en el historial.
   */
  notifyEvaFirstPlayUnconditional(): void {
    if (this.evaFirstPlayUnconditionalSentThisTurn) return;
    this.evaFirstPlayUnconditionalSentThisTurn = true;
    const nowMs = Date.now();
    if (this.turnStartAnchorTs !== null) {
      const unconditionalMs = nowMs - this.turnStartAnchorTs;
      this.sendClientMetric('first_audio_play_unconditional_ms', unconditionalMs, 'ms');
      this.firstAudioPlayUnconditionalHistory.push(unconditionalMs);
      if (this.firstAudioPlayUnconditionalHistory.length > 20) {
        this.firstAudioPlayUnconditionalHistory.shift();
      }
      if ((window as any).__fcDebug?.enabled) {
        (window as any).__fcDebug.firstAudioPlayUnconditionalMs = unconditionalMs;
      }
      console.log('[FC_METRIC] firstAudioPlayUnconditionalMs=', unconditionalMs, '(anchor=first_chunk_ts)');
    }
  }

  /**
   * Ola 3 — Envía un incremento de eva_chunks_schedule_underrun al backend.
   * Llamado desde el componente cuando el counter de underrun incrementa.
   */
  notifyEvaScheduleUnderrun(): void {
    this.sendClientMetric('eva_chunks_schedule_underrun', 1, 'count');
  }

  /**
   * Watchdog anti-deadlock — Actualiza el timestamp del último chunk de audio
   * de Eva. Llamado por el componente en `enqueueAudio` por cada chunk recibido
   * para que el watchdog sepa que Eva aún está produciendo audio.
   */
  notifyEvaAudioActivity(): void {
    this.lastEvaAudioChunkAtMs = Date.now();
  }

  /**
   * Ola 3 — Resetea el estado del funnel de métricas por turno de Eva.
   * Llamado al inicio de cada turno de Eva.
   */
  resetEvaMetricsTurn(): void {
    this.evaFirstChunkSentThisTurn = false;
    this.evaFirstPlaySentThisTurn = false;
    this.evaFirstPlayUnconditionalSentThisTurn = false;
    this.userSpeechActiveForMetric = false;
    // D2c fix — NO se resetea lastUserSpeechEndTs aquí para que el race condition
    // interrupted → resetEvaMetricsTurn → flushJitterBuffer → notifyEvaFirstPlay
    // pueda medir la latencia correctamente. lastUserSpeechEndTs se limpia solo
    // cuando la métrica lo consume (en notifyEvaFirstPlay cuando lastUserSpeechEndTs != null)
    // o cuando la sesión se desconecta. Riesgo de stale: mínimo porque el timestamp
    // anterior es un upper-bound conservador; siempre mejor que null.
    // D2 — resetear la ancla del turno para el nuevo turno.
    this.turnStartAnchorTs = null;
    // Ola 2.3 — cancelar debounce de eva_thinking al iniciar nuevo turno.
    if (this.evaThinkingDebounceTimer !== null) {
      clearTimeout(this.evaThinkingDebounceTimer);
      this.evaThinkingDebounceTimer = null;
    }
    // FC Feature 1 — cancelar cap timer al resetear turno.
    if (this.evaThinkingCapTimer !== null) {
      clearTimeout(this.evaThinkingCapTimer);
      this.evaThinkingCapTimer = null;
    }
    // Anti-eco: resetear el flag de supresión de narración para que el próximo
    // turno de Eva active una nueva ventana desde cero.
    // Bug A fix — si releaseOnPlaybackEnd está activo, la supresión fue diferida
    // al fin del playback local: NO limpiar los flags de supresión aquí porque
    // eso rompería la cadena. La limpieza ocurre en releaseOutboundSuppression
    // cuando notifyPlaybackEnd la dispara (tras el tail de 300ms).
    if (!this.releaseOnPlaybackEnd) {
      this.outboundSuppressArmed_narration = false;
      this.audioOutputSuppressedUntilMs = 0;
      this.localAudioIsPlaying = false;
      this.suppressionArmedAtMs = 0;
    }
    // narrationStartTs se resetea en releaseOutboundSuppression; aquí solo
    // garantizamos consistencia si el turno terminó por interrupted.
    this.narrationStartTs = null;
  }

  /**
   * Envía un mensaje de texto (JSON) al agente
   */
  sendMessage(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }
  
  /**
   * Detecta fin de respuesta por silencio local. El ws.send('response_complete')
   * era un huérfano (BE nunca lo procesó) y se eliminó en Ola 3.
   * Solo actualiza estado de UI (recording=false).
   */
  notifyResponseComplete(): void {
    this.recordingSubject.next(false);
  }
  
  /**
   * Finaliza la sesión
   */
  endSession(): void {
    if (this.qualityMode === 'low') {
      this.finalizingWsClose = true;
      this.disconnect();
      return;
    }
    this.sendMessage({ type: 'end_session', protocol: FC_V5_PROTOCOL });
    // Fase 7 B.4 — signal intentional teardown before closing so onclose
    // suppresses reconnect logic.
    this.finalizingWsClose = true;
    this.disconnect();
  }

  pauseSession(): void {
    this.finalizingWsClose = true;
    this.disconnect();
  }

  /**
   * Desconecta el WebSocket
   */
  disconnect(): void {
    this.rumMetrics.flush();
    if (this.lowRuntime) {
      this.lowRuntime.stop();
      this.lowRuntime = null;
    }
    // Reset mode so a later high connect() is not stuck in low routing.
    // connect()/connectLow set it again explicitly.
    this.qualityMode = 'high';

    if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout);
    }
    // FE-1 — cancelar reconnect pendiente para no disparar connect() zombie.
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearSessionAckWatchdog();
    this.clearTransitionWatchdog();

    // FC Feature 1 — cancelar cap timer al desconectar.
    if (this.evaThinkingCapTimer !== null) {
      clearTimeout(this.evaThinkingCapTimer);
      this.evaThinkingCapTimer = null;
    }
    // FC Feature 2 — limpiar buffer de reanudación al desconectar.
    this.clearResumeBuffer();
    this.lastTelemetryRollup = {};

    // Limpiar el watchdog al desconectar para no dejar intervals huérfanos.
    if (this.suppressWatchdogInterval) {
      clearInterval(this.suppressWatchdogInterval);
      this.suppressWatchdogInterval = null;
    }

    if (this.ws) {
      // F1 — desenganchar handlers ANTES de close + null, para que un onclose
      // tardío sea no-op vía epoch guard.
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      // F1 — bumpear el epoch para que cualquier callback en vuelo de este
      // socket se auto-identifique como stale.
      this.connectionEpoch++;
      // Fase 7 B.4 — mark intentional close before ws.close() so the onclose
      // handler can distinguish this from a network drop.
      this.finalizingWsClose = true;
      this.ws.close(1000, 'Client ended session');
      // Se resetea acá porque ya anulamos los handlers arriba: onclose nunca va
      // a alcanzar el path de reset del handler (epoch guard + null).
      this.finalizingWsClose = false;
      this.ws = null;
    }

    // Flush de logs coalescados pendientes del hot-path WS antes de cerrar.
    clearCoalesced();

    this.setState('completed');
  }

  /**
   * Reproduce audio recibido del agente
   */
  private async playAudio(data: Blob | ArrayBuffer): Promise<void> {
    let buffer: ArrayBuffer;
    if (data instanceof Blob) {
      buffer = await data.arrayBuffer();
    } else {
      buffer = data;
    }
    this.audioDataSubject.next(buffer);
  }
  
  /**
   * Detiene la reproducción de audio (cuando hay barge-in o cambio de
   * pregunta). El componente está suscrito a `stopAudio$` y corta el
   * AudioContext via `stopAllAudio()`.
   */
  private stopAudioPlayback(mode: AudioStopMode = 'immediate'): void {
    console.log('[FC-SERVICE] Stopping audio playback (%s)', mode);
    this.stopAudioSubject.next(mode);
  }
  
  /**
   * Detecta cuando el usuario habla (para detección de silencio)
   */
  private onUserSpeechDetected(): void {
    this.isSpeaking = true;
    this.lastSpeechTime = Date.now();
    this.resetSilenceDetection();
  }
  
  /**
   * Resetea el timeout de detección de silencio
   */
  private resetSilenceDetection(): void {
    if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout);
    }
    
    // Solo detectar silencio cuando estamos esperando respuesta
    if (this.stateSubject.value !== 'listening' || !this.micOpenSubject.value) {
      return;
    }
    
    this.silenceTimeout = setTimeout(() => {
      const silenceDuration = Date.now() - this.lastSpeechTime;
      
      if (silenceDuration >= this.silenceThresholdMs && this.isSpeaking) {
        if ((window as any).__fcVerbose) console.log('[FC-SERVICE] Silence detected, notifying agent');
        this.isSpeaking = false;
        this.notifyResponseComplete();
      }
    }, this.silenceThresholdMs);
  }
  
  /**
   * Configuración del umbral de silencio
   */
  setSilenceThreshold(ms: number): void {
    this.silenceThresholdMs = ms;
  }
  
  /**
   * Actualiza el progreso total
   */
  setTotalQuestions(total: number): void {
    this.progressSubject.next({
      current: this.progressSubject.value.current,
      total,
    });
  }
  
  /**
   * Verifica si está conectado.
   * quality_mode=low has no WebSocket — treat an active low runtime as connected
   * unless the session hit a hard error / completed terminal state (avoids the
   * shared wifi warning always showing disconnected for low).
   */
  get isConnected(): boolean {
    if (this.qualityMode === 'low') {
      const state = this.stateSubject.value;
      if (state === 'error' || state === 'completed') return false;
      return this.lowRuntime != null && !this.terminatedByEnd;
    }
    return this.ws?.readyState === WebSocket.OPEN;
  }
  
  /**
   * Estado actual
   */
  get currentState(): AgentState {
    return this.stateSubject.value;
  }

  /** Solo el host — NUNCA el query-string (SigV4 signature/token, email). */
  private redactWsUrl(raw: string): string {
    try { return new URL(raw).host; } catch { return '<redacted>'; }
  }
}
